/**
 * Medrite Check-in Machine
 *
 * Directly defines the XState machine for patient check-in flow.
 * Replaces the JSON-based flow definition + factory pattern.
 */

import { and, assign, fromPromise, setup, spawnChild } from "xstate";
import type {
  IdentityInput,
  IdentityOutput,
  InsuranceOutput,
  IntakeInput,
  IntakeOutput,
  ReturningPatientOutput,
  TriageOutput,
} from "../machine/contracts/arc-contracts";
import type { KioskMachineContext, KioskMachineEvent } from "../machine/types";

import { mapReturningPatientAddress } from "../machine/actions/map-returning-patient-address.action";
import { identityArc } from "../machine/arcs/identity.arc";
import { insuranceArc } from "../machine/arcs/insurance.arc";
import { intakeArc } from "../machine/arcs/intake.arc";
import { returningPatientArc } from "../machine/arcs/returning-patient.arc";
// Arc machines
import { triageArc } from "../machine/arcs/triage.arc";
import { mapAddressToResponses } from "../machine/helper/address-response-mapper";
import { mergeArcOutput } from "../machine/helper/arc-helpers";
import { deriveIntakeSkipFlags } from "../machine/helper/derive-intake-skip-flags";
import {
  assembleInsuranceArcInput,
  buildInsuranceArrayForPhotoUpload,
} from "../machine/helper/insurance-helpers";

import { analyzeOcrActor } from "../machine/actors/analyze-ocr.actor";
import { createAppointmentWithBoughtTreatmentsActor } from "../machine/actors/create-appointment-with-bought-treatments.actor";
import { createInsuranceActor } from "../machine/actors/create-insurance.actor";
import { createPaymentIntentActor } from "../machine/actors/create-payment-intent.actor";
import { createSetupIntentActor } from "../machine/actors/create-setup-intent.actor";
import { createUserAndConsumerActor } from "../machine/actors/create-user-and-consumer.actor";
import { fetchConsumerInsuranceActor } from "../machine/actors/fetch-consumer-insurance.actor";
import { fetchPricingActor } from "../machine/actors/fetch-pricing-only.actor";
import { fetchSavedPaymentMethodActor } from "../machine/actors/fetch-saved-payment-method.actor";
import { loadHrtsActor } from "../machine/actors/load-hrts.actor";
import { posHealthMonitorActor } from "../machine/actors/pos-health-monitor.actor";
import { processCardOnFilePaymentActor } from "../machine/actors/process-card-on-file-payment.actor";
import { processPOSPaymentActor } from "../machine/actors/process-pos-payment.actor";
import { processStripeElementPaymentActor } from "../machine/actors/process-stripe-element-payment.actor";
import { recordCardOnFileConsentActor } from "../machine/actors/record-card-on-file-consent.actor";
import { replaceBoughtTreatmentActor } from "../machine/actors/replace-bought-treatment.actor";
// Import actors
import { sendSMSActor } from "../machine/actors/send-sms.actor";
import { setupIntakeActor } from "../machine/actors/setup-intake.actor";
import { setupPhotosActor } from "../machine/actors/setup-photos.actor";
import { submitIntakeFormsActor } from "../machine/actors/submit-intake-forms.actor";
import { updateConsumerDataActor } from "../machine/actors/update-consumer-data.actor";
import { uploadConsumerPhotosActor } from "../machine/actors/upload-consumer-photos.actor";
import { verifySMSActor } from "../machine/actors/verify-sms.actor";
import { markAsArrived } from "../server-actions/pc-actions";

// Import guards
import { shouldRecordCardOnFileConsent } from "../machine/guards/card-on-file-consent.guard";
import {
  addingInsuranceFromStorefront,
  contactTypeIsPhone,
  hasExistingAppointment,
  hasHrtsLoaded,
  hasSavedPaymentMethod,
  hasSelectedAppointment,
  hasUserId,
  isDataAlreadyWritten,
  isDataWriteBeforePayment,
  isEhrLocationIdMissing,
  isFrontDeskMode,
  isKioskMode,
  isPracticeIdMissing,
  isQRMode,
  isRoutedFromEmailMatch,
  isTreatmentChanged,
  isUnder18,
  requiresSetupIntent,
  shouldCheckSavedCard,
  targetIsDefaultTreatment,
  targetIsEpsTreatment,
  targetIsSpecificTreatment,
  targetIsTravelInsuranceTreatment,
  targetIsWorkersCompTreatment,
} from "../machine/guards/context.guard";
import { hasOcrData } from "../machine/guards/has-ocr-data.guard";
import { hasPhysicalId } from "../machine/guards/has-physical-id.guard";
import {
  createPricingInputSnapshot,
  hasInsuranceInContext,
  hasInsuranceInputChanged,
  hasInsurancePricingData,
  hasPricingData,
  isAtLeastPrimaryInsuranceActive,
  noInsuranceInContext,
  pricingFetchFailed,
  pricingFetchSuccess,
} from "../machine/guards/insurance.guard";
import {
  isPOSAvailable,
  isPOSDegraded,
  isPOSHealthy,
  isPOSOffline,
  isPOSUnavailable,
  shouldFallbackToManualEntry,
  shouldUsePOSPayment,
} from "../machine/guards/pos-health.guard";
import {
  isSmsAuthenticated,
  otpInvalid,
  smsReturnedUserId,
} from "../machine/guards/sms.guard";

import { datadogLogs } from "@datadog/browser-logs";
import { ErrorCode } from "@frontend/lib/utils/insuranceErrorResolver";
import { getKioskId } from "../lib/kiosk-identity";
import { CARD_AUTH_RECORDING_TIMEOUT_MS } from "../lib/payment/card-on-file-timing";
import { calculatePaymentAmount } from "../machine/actions/calculate-payment-amount.action";
import { clearContactInfo } from "../machine/actions/clear-contact-info.action";
import {
  clearInsurance,
  clearInsuredPricing,
  clearSecondaryInsurance,
  restoreInsuranceIdsFromBackground,
} from "../machine/actions/clear-insurance.action";
import { clearUserIds } from "../machine/actions/clear-user-ids.action";
import { restartFlow } from "../machine/actions/restart-flow.action";
import { saveActorResult } from "../machine/actions/save-actor-result.action";
import { saveError } from "../machine/actions/save-error.action";
// Import actions
import { saveFormData } from "../machine/actions/save-form-data.action";
import { saveInsuranceIds } from "../machine/actions/save-insurance-ids.action";
import { savePhotoData } from "../machine/actions/save-photo-data.action";
import { buildIdentityEditSyncPatch } from "../machine/actions/sync-identity-edit-to-returning-patient.action";
import { updatePendingPricesMode } from "../machine/actions/update-pending-prices-mode.action";
import {
  logPOSHealthWarning,
  setPOSDegraded,
  setPOSHealthy,
  setPOSNoReader,
  setPOSOffline,
} from "../machine/actions/update-pos-health.action";
import {
  hasInfoConfirmed,
  isReviewMode,
} from "../machine/guards/intake-go-back.guard";
import { logFlowCompleteAction } from "./actions/log-flow-complete.action";
import { logNotConfiguredAction } from "./actions/log-not-configured.action";
import {
  type HrtType,
  createClearedPaymentContext,
  createDefaultTreatmentContextUpdate,
  createSpecificTreatmentContext,
  extractAppointmentData,
  extractInsuranceIds,
  extractPricingData,
  getEffectiveHrt,
  isTreatmentChangedFromEvent,
} from "./helper";

export type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

/**
 * Initial context type
 */
export interface MedriteCheckinInitialContext {
  qrMode?: boolean;
  pendingPricesMode?: boolean;
  practiceId?: string;
  appointmentId?: string;
  ehrLocationId?: string;
  orgId?: number;
  orgName?: string;
  stripeReaderId?: string;
  navigationMode?: string;
  permissionRequestMode?: string;
}

/**
 * Factory function to create a fresh initial context
 * Used for machine initialization and restart flow
 */
export const createInitialContext = (
  overrides?: MedriteCheckinInitialContext,
) => ({
  qrMode: overrides?.qrMode ?? false,
  pendingPricesMode: overrides?.pendingPricesMode ?? false,
  orgId: overrides?.orgId ?? 24,
  // No hardcoded fallbacks: a missing practiceId or ehrLocationId must fail safe
  // (the machine routes to `notConfigured`) rather than silently booking to a
  // real practice or writing patient records against a default EHR location.
  practiceId: overrides?.practiceId,
  ehrLocationId: overrides?.ehrLocationId,
  orgName: overrides?.orgName ?? "Medrite",
  language: "en",
  appointmentId: overrides?.appointmentId,
  stripeReaderId: overrides?.stripeReaderId,
  navigationMode: overrides?.navigationMode ?? "window",
  permissionRequestMode: overrides?.permissionRequestMode ?? "startup",
  pricingMap: {},
  paymentRoute: null,
  targetTreatmentState: undefined,
  preDataWriteState: undefined,
  frontDeskMode: undefined,
});

/**
 * Build the input for submitIntakeForms actor.
 * Shared by submittingIntakeForms (storefront) and submittingReturningIntake (appointment).
 */
// biome-ignore lint/suspicious/noExplicitAny: KioskMachineContext is wide
export function buildSubmitIntakeInput(context: any, appointmentId: string) {
  const sections = (context.intakeFormSections || []) as Array<{
    formId: string;
    sectionName: string;
    sectionText: string;
    priority: number;
    sectionTranslations: unknown[];
    questions: Array<{ intakeQuestion: { id: number } }>;
  }>;
  // Only submit forms the patient actually completed during intake.
  // submittedFormIds (from intake arc output) is the source of truth when available.
  // Falls back to section-based filtering for edge cases where submittedFormIds is empty.
  const submitted = (context.submittedFormIds || []) as string[];
  const sectionFormIds = new Set(sections.map((s) => s.formId));
  const formIds = ((context.formIds || []) as number[]).filter((id: number) => {
    if (submitted.length > 0) {
      return submitted.includes(id.toString());
    }
    return sectionFormIds.has(id.toString());
  });

  return {
    formIds,
    intakeFormSections: sections as Array<{
      formId: string;
      sectionName: string;
      sectionText: string;
      priority: number;
      sectionTranslations: unknown[];
      questions: Array<{
        intakeQuestion: {
          id: number;
          type: string;
          questionText: string;
          originalText?: string;
          questionNumber: number;
          questionAttributes: unknown;
          originalAttributes?: unknown;
          intakeFormSection?: {
            intakeFormSectionTranslations?: Array<{
              languageCode: string;
              text: string;
            }>;
          };
        };
      }>;
    }>,
    intakeResponses: (context.intakeResponses ||
      context.responses ||
      {}) as Record<string, unknown>,
    appointmentId,
    consumerId: context.consumerId as string,
    userId: context.userId as string,
    orgId: context.orgId as number,
    practiceId: Number(context.practiceId),
    language: "en",
    consumer: {
      firstname: context.firstName as string,
      lastname: context.lastName as string,
      dob: context.dateOfBirth as string,
      ehrPatientId: context.ehrPatientId,
      ehrEnterpriseId: undefined,
    },
  };
}

/**
 * Create the medrite check-in machine
 */
export const createMedriteCheckinMachine = (
  initialContext?: MedriteCheckinInitialContext,
) => {
  return setup({
    types: {
      context: {} as KioskMachineContext,
      events: {} as KioskMachineEvent,
    },
    actors: {
      // Arc machines
      triageArc: triageArc,
      returningPatientArc: returningPatientArc,
      identityArc: identityArc,
      insuranceArc: insuranceArc,
      intakeArc: intakeArc,
      // Actors
      fetchConsumerInsurance: fetchConsumerInsuranceActor.impl,
      sendSMS: sendSMSActor.impl,
      verifySMS: verifySMSActor.impl,
      setupIntake: setupIntakeActor.impl,
      setupPhotos: setupPhotosActor.impl,
      loadHrts: loadHrtsActor.impl,
      fetchPricing: fetchPricingActor.impl,
      createPaymentIntent: createPaymentIntentActor.impl,
      createSetupIntent: createSetupIntentActor.impl,
      processPOSPayment: processPOSPaymentActor.impl,
      processStripeElementPayment: processStripeElementPaymentActor.impl,
      processCardOnFilePayment: processCardOnFilePaymentActor.impl,
      recordCardOnFileConsent: recordCardOnFileConsentActor.impl,
      createUserAndConsumer: createUserAndConsumerActor.impl,
      updateConsumerData: updateConsumerDataActor.impl,
      createInsurance: createInsuranceActor.impl,
      createAppointmentWithBoughtTreatments:
        createAppointmentWithBoughtTreatmentsActor.impl,
      submitIntakeForms: submitIntakeFormsActor.impl,
      uploadConsumerPhotos: uploadConsumerPhotosActor.impl,
      analyzeOcr: analyzeOcrActor.impl,
      fetchSavedPaymentMethod: fetchSavedPaymentMethodActor.impl,
      replaceBoughtTreatment: replaceBoughtTreatmentActor.impl,
      posHealthMonitor: posHealthMonitorActor.impl,
    },
    guards: {
      hasUserId: hasUserId.impl,
      hasHrtsLoaded: hasHrtsLoaded.impl,
      otpInvalid: otpInvalid.impl,
      smsReturnedUserId: smsReturnedUserId.impl,
      isSmsAuthenticated: isSmsAuthenticated.impl,
      hasPhysicalId: hasPhysicalId.impl,
      hasOcrData: hasOcrData.impl,
      contactTypeIsPhone: contactTypeIsPhone.impl,
      hasInsuranceInContext: hasInsuranceInContext.impl,
      noInsuranceInContext: noInsuranceInContext.impl,
      pricingFetchSuccess: pricingFetchSuccess.impl,
      pricingFetchFailed: pricingFetchFailed.impl,
      addingInsuranceFromStorefront: addingInsuranceFromStorefront.impl,
      hasPricingData: hasPricingData.impl,
      hasInsurancePricingData: hasInsurancePricingData.impl,
      isQRMode: isQRMode.impl,
      isKioskMode: isKioskMode.impl,
      isRoutedFromEmailMatch: isRoutedFromEmailMatch.impl,
      requiresSetupIntent: requiresSetupIntent.impl,
      shouldCheckSavedCard: shouldCheckSavedCard.impl,
      shouldRecordCardOnFileConsent: shouldRecordCardOnFileConsent.impl,
      hasSavedPaymentMethod: hasSavedPaymentMethod.impl,
      isDataAlreadyWritten: isDataAlreadyWritten.impl,
      isDataWriteBeforePayment: isDataWriteBeforePayment.impl,
      isFrontDeskMode: isFrontDeskMode.impl,
      isPracticeIdMissing: isPracticeIdMissing.impl,
      isEhrLocationIdMissing: isEhrLocationIdMissing.impl,
      targetIsDefaultTreatment: targetIsDefaultTreatment.impl,
      targetIsSpecificTreatment: targetIsSpecificTreatment.impl,
      targetIsWorkersCompTreatment: targetIsWorkersCompTreatment.impl,
      targetIsEpsTreatment: targetIsEpsTreatment.impl,
      targetIsTravelInsuranceTreatment: targetIsTravelInsuranceTreatment.impl,
      isTreatmentChanged: isTreatmentChanged.impl,
      isUnder18: isUnder18.impl,
      hasExistingAppointment: hasExistingAppointment.impl,
      hasSelectedAppointment: hasSelectedAppointment.impl,
      // POS Health guards
      isPOSHealthy: isPOSHealthy.impl,
      isPOSDegraded: isPOSDegraded.impl,
      isPOSOffline: isPOSOffline.impl,
      isPOSAvailable: isPOSAvailable.impl,
      isPOSUnavailable: isPOSUnavailable.impl,
      shouldUsePOSPayment: shouldUsePOSPayment.impl,
      shouldFallbackToManualEntry: shouldFallbackToManualEntry.impl,
    },
    actions: {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      saveFormData: saveFormData.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      saveError: saveError.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      saveActorResult: saveActorResult.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      savePhotoData: savePhotoData.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      restartFlow: restartFlow.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      clearInsurance: clearInsurance.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      clearInsuredPricing: clearInsuredPricing.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      clearSecondaryInsurance: clearSecondaryInsurance.impl as any,
      restoreInsuranceIdsFromBackground:
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        restoreInsuranceIdsFromBackground.impl as any,
      logError: ({ event }) => {
        console.error("[Machine] Error in optional actor:", event);
      },
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      clearUserIds: clearUserIds.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      clearContactInfo: clearContactInfo.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      saveInsuranceIds: saveInsuranceIds.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      updatePendingPricesMode: updatePendingPricesMode.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      calculatePaymentAmount: calculatePaymentAmount.impl as any,
      // POS Health actions
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      setPOSHealthy: setPOSHealthy.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      setPOSDegraded: setPOSDegraded.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      setPOSOffline: setPOSOffline.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      setPOSNoReader: setPOSNoReader.impl as any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      logPOSHealthWarning: logPOSHealthWarning.impl as any,
    },
  }).createMachine({
    id: "medrite-checkin",
    initial: "welcome",
    context: createInitialContext(initialContext),
    // Global event handlers that work from ANY state
    on: {
      RESTART: {
        target: ".welcome",
        actions: ["restartFlow"],
      },
      GO_TO_STANDBY: {
        target: ".standby",
      },
      UPDATE_PENDING_PRICES_MODE: {
        actions: ["updatePendingPricesMode"],
      },
      // POS Health events - handled globally so any state can receive updates
      "POS_HEALTH.HEALTHY": {
        actions: ["setPOSHealthy"],
      },
      "POS_HEALTH.DEGRADED": {
        actions: ["setPOSDegraded"],
      },
      "POS_HEALTH.OFFLINE": {
        actions: ["setPOSOffline"],
      },
      "POS_HEALTH.NO_READER": {
        actions: ["setPOSNoReader"],
      },
      // Reprice completes on the parent-owned actor — store result for insuring input assembly
      "xstate.done.actor.fetchConsumerInsurance": {
        // biome-ignore lint/suspicious/noExplicitAny: XState spawned child done event typing
        actions: assign(({ event }: { event: any }) => ({
          _returningPatientPrimaryInsurance:
            event.output?.primaryInsurance ?? null,
          _returningPatientSecondaryInsurance:
            event.output?.secondaryInsurance ?? null,
        })),
      },
      // Reprice failed — treat as unavailable so insuring falls back to stale match data
      "xstate.error.actor.fetchConsumerInsurance": {
        actions: assign({
          _returningPatientPrimaryInsurance: null,
          _returningPatientSecondaryInsurance: null,
        }),
      },
    },
    // POS health monitor runs for entire machine lifetime (not tied to a single state)
    invoke: {
      id: "posHealthMonitor",
      src: "posHealthMonitor",
      input: ({ context }) => ({
        readerId: context.stripeReaderId as string | undefined,
        pollIntervalMs: 30000, // Poll every 30 seconds
        degradedThresholdMs: 60000, // Degraded if not seen in 60s
      }),
    },
    states: {
      standby: {
        meta: {
          componentName: "Standby",
          contentKey: "medrite.checkin.standby",
        },
        on: {
          START: { target: "welcome" },
        },
      },
      welcome: {
        // Fail-safe: block an unconfigured kiosk (no resolved practiceId or
        // ehrLocationId) before it can start a check-in, instead of defaulting
        // to a real practice or a hardcoded EHR location.
        always: [
          { guard: "isPracticeIdMissing", target: "notConfigured" },
          { guard: "isEhrLocationIdMissing", target: "notConfigured" },
        ],
        meta: {
          componentName: "Welcome",
          contentKey: "medrite.checkin.welcome",
        },
        on: {
          START: { target: "languageSelect" },
        },
      },

      // Dead-end block state for a kiosk that has no resolved practiceId. There
      // is no transition out by design: the tablet self-heals only when it is
      // relaunched with a correct `?practiceId=` in the start URL (which resets
      // the machine and rewrites the cached practiceId).
      notConfigured: {
        // LOUD fail-safe telemetry: a required launch id (practiceId and/or
        // ehrLocationId) was missing, so emit an alertable Datadog error the
        // moment we dead-end here — otherwise a misconfigured kiosk fails
        // silently to the front-desk block screen with no prod signal.
        entry: logNotConfiguredAction,
        meta: {
          componentName: "NotConfigured",
          contentKey: "medrite.checkin.not-configured",
        },
      },

      languageSelect: {
        meta: {
          componentName: "LanguageSelect",
          contentKey: "medrite.checkin.language-select",
        },
        on: {
          SUBMIT: {
            target: "triaging",
            actions: ["saveFormData"],
          },
        },
      },

      // ===== TRIAGE ARC =====
      triaging: {
        invoke: {
          src: "triageArc",
          id: "triageArc",
          input: ({ context }) => ({
            orgId: context.orgId as number,
            orgName: context.orgName as string,
            practiceId: context.practiceId as string,
            language: context.language,
            previousOutput: context._triageOutput as TriageOutput | undefined,
          }),
          onDone: {
            target: "loadingPrerequisites",
            actions: [
              mergeArcOutput<TriageOutput>("_triageOutput"),
              // Answering "no" (or switching to travel insurance) invalidates
              // insurance data cached from a previous pass — e.g. the user went
              // BACK after the insurance arc and changed their triage answer.
              assign(({ event }) =>
                event.output.planToUseInsurance === false ||
                event.output.isTravelInsurance === true
                  ? {
                      insurances: undefined,
                      insuranceVerification: undefined,
                      insuranceStates: undefined,
                      _insuranceOutput: undefined,
                      _primaryReviewUsed: undefined,
                      _secondaryReviewUsed: undefined,
                      _secondaryReviewNeeded: undefined,
                      addingInsuranceFromStorefront: undefined,
                      addedInsuranceFromStorefront: undefined,
                    }
                  : {},
              ),
            ],
          },
        },
        on: {
          // Arc boundary signal: child sends GO_BACK from first state
          // Clear cached output so re-entering triage starts from proxyQuestion
          GO_BACK: {
            target: "languageSelect",
            actions: assign({ _triageOutput: undefined }),
          },
        },
      },

      loadingPrerequisites: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.loading-prerequisites",
        },
        invoke: [
          {
            id: "setupIntake",
            src: "setupIntake",
            input: ({ context }) => ({
              userId: context.userId,
              consumerId: context.consumerId,
              orgId: context.orgId,
              practiceId: context.practiceId,
              sex: context.sex,
            }),
            onDone: {
              actions: ["saveActorResult"],
              target: "returningPatient",
            },
            onError: {
              target: "prerequisitesError",
              actions: ["saveError"],
            },
          },
        ],
      },

      // ===== RETURNING PATIENT ARC =====
      returningPatient: {
        invoke: {
          src: "returningPatientArc",
          id: "returningPatientArc",
          input: ({ context }) => ({
            orgId: context.orgId as number,
            orgName: context.orgName as string,
            practiceId: context.practiceId as string,
            language: context.language,
            intakeFormSections:
              context.intakeFormSections as ReturningPatientOutput["intakeFormSections"],
            responses: context.responses as ReturningPatientOutput["responses"],
            formIds: context.formIds as ReturningPatientOutput["formIds"],
            newPatientPath: context._newPatientPath as boolean | undefined,
            phoneNumber: context.phoneNumber as string | undefined,
            dateOfBirth: context.dateOfBirth as string | undefined,
            previousOutput: context._returningPatientOutput as
              | ReturningPatientOutput
              | undefined,
            emailMatchMode: context._routedFromEmailMatch as
              | boolean
              | undefined,
            emailForMatch: context._routedFromEmailMatch
              ? (context.email as string | undefined)
              : undefined,
            firstNameForMatch: context._routedFromEmailMatch
              ? (context.firstName as string | undefined)
              : undefined,
            lastNameForMatch: context._routedFromEmailMatch
              ? (context.lastName as string | undefined)
              : undefined,
            planToUseInsurance: context.planToUseInsurance as
              | boolean
              | undefined,
            isTravelInsurance: context.isTravelInsurance as boolean | undefined,
            under18: context.under18 as boolean | undefined,
            checkingInForSomeoneElse: context.checkingInForSomeoneElse as
              | boolean
              | undefined,
          }),
          onDone: [
            {
              // Identity fields missing → verify mode (show only missing identity screens)
              guard: ({ event }) => {
                const output = event.output as ReturningPatientOutput;
                return (
                  !output.firstName ||
                  !output.lastName ||
                  !output.sex ||
                  !output.email ||
                  !output.address
                );
              },
              target: "identifying",
              actions: [
                mergeArcOutput<ReturningPatientOutput>(
                  "_returningPatientOutput",
                ),
                assign({
                  _reviewMode: true,
                  _verifyMode: true,
                  // Clear stale identity output so verify mode starts fresh
                  _identityOutput: undefined,
                  // Missing identity invalidates any prior confirmation —
                  // don't let a stale `infoConfirmed: true` from a previous
                  // session leak into intake skip-flag derivation.
                  _returningPatientOutput: ({ context }) => ({
                    ...(context._returningPatientOutput as ReturningPatientOutput),
                    infoConfirmed: false,
                  }),
                }),
                mapReturningPatientAddress,
              ],
            },
            {
              // All identity fields present → skip identity
              target: "loadingPhotos",
              actions: [
                mergeArcOutput<ReturningPatientOutput>(
                  "_returningPatientOutput",
                ),
                mapReturningPatientAddress,
              ],
            },
          ],
        },
        on: {
          // Arc boundary signal: child sends GO_BACK from phoneInput (or OTP in email match mode)
          GO_BACK: [
            {
              // Email match mode: BACK from OTP → re-invoke identity arc at emailInput
              guard: "isRoutedFromEmailMatch",
              target: "identifying",
              // biome-ignore lint/suspicious/noExplicitAny: XState guard context typing
              actions: assign(({ context }: { context: any }) => ({
                _routedFromEmailMatch: undefined,
                _resumeAtEmailInput: true,
                _identityOutput: {
                  firstName: context.firstName ?? "",
                  lastName: context.lastName ?? "",
                  dateOfBirth: context.dateOfBirth ?? "",
                  sex: "",
                  email: context.email,
                  responses: context.responses,
                  licensePhotoFront: context.licensePhotoFront,
                  licensePhotoBack: context.licensePhotoBack,
                  passportPhoto: context.passportPhoto,
                  hasPhysicalId: context.hasPhysicalId,
                  idType: context.idType,
                  ocrData: context.ocrData,
                },
              })),
            },
            {
              // Normal: BACK from phoneInput → triaging
              // Preserve _returningPatientOutput: triage answers don't affect patient
              // matching, so clearing it would force the user to redo SMS+OTP
              // verification unnecessarily after changing a triage answer.
              target: "triaging",
              // actions: assign({ _returningPatientOutput: undefined }),
            },
          ],
          // Email match failed: send back to identity arc to continue from emailInput
          EMAIL_MATCH_FAILED: {
            target: "identifying",
            actions: assign(
              // biome-ignore lint/suspicious/noExplicitAny: XState cross-arc signal event typing
              ({ context, event }: { context: any; event: any }) => ({
                _routedFromEmailMatch: undefined,
                _returningPatientOutput: undefined,
                _resumeAtEmailInput: undefined,
                // Carry otpDeclined from event (set when patient skips OTP in email match mode)
                otpDeclined: event.otpDeclined ?? undefined,
                // Partial _identityOutput so hasCompletedBefore guard routes to sexInput
                // Preserve photo data captured before the email match detour
                _identityOutput: {
                  firstName: context.firstName ?? "",
                  lastName: context.lastName ?? "",
                  dateOfBirth: context.dateOfBirth ?? "",
                  sex: "",
                  email: context.email,
                  responses: context.responses,
                  licensePhotoFront: context.licensePhotoFront,
                  licensePhotoBack: context.licensePhotoBack,
                  passportPhoto: context.passportPhoto,
                  hasPhysicalId: context.hasPhysicalId,
                  idType: context.idType,
                  ocrData: context.ocrData,
                },
              }),
            ),
          },
          // Arc signals parent to spawn the reprice actor (owned by parent, survives arc exit)
          SPAWN_REPRICE: {
            actions: spawnChild("fetchConsumerInsurance", {
              id: "fetchConsumerInsurance",
              // biome-ignore lint/suspicious/noExplicitAny: XState cross-arc sendParent event typing
              input: ({ event }: { event: any }) => ({
                consumerId: event.consumerId as string,
                orgId: event.orgId as number,
                firstName: event.firstName as string,
                lastName: event.lastName as string,
                dateOfBirth: event.dateOfBirth as string,
              }),
            }),
          },
          // Patient chose new patient flow from newPatientInterstitial
          NEW_PATIENT: {
            target: "identifying",
            // biome-ignore lint/suspicious/noExplicitAny: XState event typing for cross-arc signal
            actions: assign(({ event }: { event: any }) => ({
              dateOfBirth: event.dateOfBirth,
              phoneNumber: event.phoneNumber,
              otpDeclined: event.otpDeclined,
              _newPatientPath: true,
              _routedFromEmailMatch: undefined,
              // PLA-1715: forward matched IDs from arc into background slots so
              // the createUserAndConsumer actor's dedupe branch returns them
              // instead of creating a duplicate user/consumer on the backend.
              userId: undefined,
              consumerId: undefined,
              userIdBackground: event.userId,
              consumerIdBackground: event.consumerId,
              // Preserve the matched ehrPatientId so submit-intake-forms can
              // succeed. The dedupe branch in createUserAndConsumer returns
              // only { userId, consumerId } — without this, ehrPatientId
              // would be lost and intake submission would fail with
              // "EHR patient ID or EHR enterprise ID is required".
              ehrPatientId: event.ehrPatientId,
              // Clear ALL returning patient data — demographics, cached outputs,
              // insurance, flags — so new patient flow starts completely fresh
              _returningPatientOutput: undefined,
              _returningPatientPrimaryInsurance: null,
              _returningPatientSecondaryInsurance: null,
              insurances: undefined,
              _insuranceOutput: undefined,
              _identityOutput: undefined,
              _intakeOutput: undefined,
              _reviewMode: undefined,
              _verifyMode: undefined,
              // Clear all demographics spread from returning patient arc
              firstName: undefined,
              lastName: undefined,
              sex: undefined,
              email: undefined,
              address: undefined,
              addressLine2: undefined,
              city: undefined,
              state: undefined,
              zip: undefined,
              responses: undefined,
              // Pharmacy
              pharmacyName: undefined,
              pharmacyPhone: undefined,
              pharmacyStreetAddress: undefined,
              pharmacyCity: undefined,
              pharmacyState: undefined,
              pharmacyZip: undefined,
              pharmacyEhrId: undefined,
              pharmacyNcpdpId: undefined,
              // Emergency contact
              emergencyContactFirstName: undefined,
              emergencyContactLastName: undefined,
              emergencyContactPhone: undefined,
              emergencyContactRelationship: undefined,
              // PCP
              pcpFirstName: undefined,
              pcpLastName: undefined,
              pcpPhone: undefined,
              pcpAddress: undefined,
              pcpCity: undefined,
              pcpState: undefined,
              pcpZip: undefined,
              pcpEhrId: undefined,
              // Appointment
              selectedAppointmentId: undefined,
              selectedEhrAppointmentId: undefined,
              selectedAppointment: undefined,
            })),
          },
          // Patient wants to update info from infoReview
          GO_TO_UPDATE: {
            target: "identifying",
            actions: assign(
              // biome-ignore lint/suspicious/noExplicitAny: XState event typing for cross-arc signal
              ({ context, event }: { context: any; event: any }) => {
                // Extract the full arc output from the signal (minus `type`)
                const { type: _, ...arcOutput } = event;
                return {
                  // Merge all arc output fields onto parent context
                  ...arcOutput,
                  // Cache for re-invocation, same as mergeArcOutput would
                  _returningPatientOutput: arcOutput,
                  _reviewMode: true,
                  // Clear cached outputs so edit flow starts fresh
                  _identityOutput: undefined,
                  _intakeOutput: undefined,
                  // Map flat address fields into responses by intake question ID
                  responses: mapAddressToResponses(
                    context.intakeFormSections,
                    context.responses ?? {},
                    {
                      street: event.address,
                      city: event.city,
                      state: event.state,
                      zip: event.zip,
                      line2: event.addressLine2,
                    },
                  ),
                };
              },
            ),
          },
        },
      },

      // ===== IDENTITY ARC =====
      identifying: {
        invoke: {
          src: "identityArc",
          id: "identityArc",
          input: ({ context }) => ({
            orgId: context.orgId as number,
            orgName: context.orgName as string,
            practiceId: context.practiceId as string,
            language: context.language,
            under18: context.under18 as boolean | undefined,
            dateOfBirth: context.dateOfBirth as string | undefined,
            intakeFormSections:
              context.intakeFormSections as IdentityInput["intakeFormSections"],
            responses: context.responses as IdentityInput["responses"],
            formIds: context.formIds as IdentityInput["formIds"],
            reviewMode: context._reviewMode as boolean | undefined,
            verifyMode: context._verifyMode as boolean | undefined,
            previousOutput: context._identityOutput as
              | IdentityOutput
              | undefined,
            // Demographics for review/verify mode first entry (pre-fill without previousOutput)
            firstName: context.firstName as string | undefined,
            lastName: context.lastName as string | undefined,
            sex: context.sex as string | undefined,
            email: context.email as string | undefined,
            otpDeclined: context.otpDeclined as boolean | undefined,
            verified: context.verified as boolean | undefined,
            phoneNumber: context.phoneNumber as string | undefined,
            resumeAtEmailInput: context._resumeAtEmailInput as
              | boolean
              | undefined,
            qrMode: context.qrMode as boolean | undefined,
          }),
          onDone: {
            target: "loadingPhotos",
            actions: [
              mergeArcOutput<IdentityOutput>("_identityOutput"),
              assign({
                _newPatientPath: () => undefined,
                _resumeAtEmailInput: () => undefined,
              }),
              // Review/verify mode: sync edited identity fields back into
              // `_consumer*` display storage and the cached
              // `_returningPatientOutput` so the infoReview card (reached via
              // BACK from intake or a future re-invocation) shows fresh
              // values instead of the pre-edit snapshot. No-op for new
              // patients (`_reviewMode` undefined). See
              // `sync-identity-edit-to-returning-patient.action.ts` for the
              // pure patch builder the unit tests exercise directly.
              assign(buildIdentityEditSyncPatch),
            ],
          },
        },
        on: {
          // Arc boundary signal: child sends GO_BACK from first state
          // (idTypeSelection for new patients, or verifyBack with empty
          // history for verify-mode users walking backward).
          GO_BACK: {
            target: "returningPatient",
            actions: assign({
              _reviewMode: () => undefined,
              // Clear verify mode too — the verify pass is complete and
              // the user is exiting identity via BACK. If this isn't
              // cleared, a later re-entry would erroneously trigger the
              // verify-back route again.
              _verifyMode: () => undefined,
            }),
          },
          // Email match signal: child sends MATCH_BY_EMAIL from emailInput
          MATCH_BY_EMAIL: {
            target: "returningPatient",
            actions: assign(
              // biome-ignore lint/suspicious/noExplicitAny: XState cross-arc signal event typing
              ({ context, event }: { context: any; event: any }) => ({
                email: event.email,
                firstName: event.firstName,
                lastName: event.lastName,
                dateOfBirth: event.dateOfBirth ?? context.dateOfBirth,
                phoneNumber: event.phoneNumber ?? context.phoneNumber,
                responses: event.responses ?? context.responses,
                _routedFromEmailMatch: true,
                _newPatientPath: undefined,
                _reviewMode: undefined,
                // Preserve photo data from the destroyed identity arc
                licensePhotoFront: event.licensePhotoFront,
                licensePhotoBack: event.licensePhotoBack,
                passportPhoto: event.passportPhoto,
                hasPhysicalId: event.hasPhysicalId,
                idType: event.idType,
                ocrData: event.ocrData,
                // Build partial _identityOutput so re-invoked arc gets photos via previousOutput
                _identityOutput:
                  event.licensePhotoFront ||
                  event.licensePhotoBack ||
                  event.passportPhoto
                    ? {
                        firstName: event.firstName,
                        lastName: event.lastName,
                        dateOfBirth: event.dateOfBirth ?? context.dateOfBirth,
                        sex: context.sex ?? "",
                        licensePhotoFront: event.licensePhotoFront,
                        licensePhotoBack: event.licensePhotoBack,
                        passportPhoto: event.passportPhoto,
                        hasPhysicalId: event.hasPhysicalId,
                        idType: event.idType,
                        ocrData: event.ocrData,
                        email: event.email,
                        responses: event.responses ?? context.responses,
                      }
                    : undefined,
              }),
            ),
          },
        },
      },

      loadingPhotos: {
        // On the new-patient path the NEW_PATIENT handler clears
        // context.consumerId (stashing it in consumerIdBackground), so there is
        // no id with which to fetch existing photo metadata. Skip straight to
        // `intaking` — the same target setupPhotos' onDone/onError use — so the
        // actor is never invoked with an undefined consumerId, which would emit
        // a prod GraphQL error: required variable "$consumerId" not provided.
        always: [
          {
            guard: ({ context }) => !context.consumerId,
            target: "intaking",
          },
        ],
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.loading-prerequisites",
        },
        invoke: {
          id: "setupPhotos",
          src: "setupPhotos",
          input: ({ context }) => ({
            consumerId: context.consumerId,
            orgId: context.orgId,
          }),
          onDone: {
            actions: ["saveActorResult"],
            target: "intaking",
          },
          onError: {
            // Photos are optional - don't fail the flow if they can't be loaded
            actions: ["logError"],
            target: "intaking",
          },
        },
      },

      prerequisitesError: {
        meta: {
          componentName: "ErrorDisplay",
          contentKey: "medrite.checkin.prerequisites-error",
        },
        on: {
          RETRY: { target: "loadingPrerequisites" },
          BACK: { target: "triaging" },
        },
      },

      // ===== INTAKE ARC =====
      intaking: {
        invoke: {
          src: "intakeArc",
          id: "intakeArc",
          input: ({ context }) => ({
            orgId: context.orgId as number,
            intakeFormSections:
              context.intakeFormSections as IntakeInput["intakeFormSections"],
            responses: context.responses as IntakeInput["responses"],
            formIds: context.formIds as IntakeInput["formIds"],
            firstName: context.firstName as string | undefined,
            lastName: context.lastName as string | undefined,
            // Returning patient routing: set when we have returning patient data
            isReturningPatient: (context._returningPatientOutput as
              | ReturningPatientOutput
              | undefined)
              ? true
              : undefined,
            hipaaExpired: (
              context._returningPatientOutput as
                | ReturningPatientOutput
                | undefined
            )?.hipaaExpired,
            // Per-screen skip flags: only set on "Looks Good" path
            ...deriveIntakeSkipFlags(
              context._returningPatientOutput as
                | ReturningPatientOutput
                | undefined,
            ),
            previousOutput: context._intakeOutput as IntakeOutput | undefined,
          }),
          onDone: [
            {
              // Returning patient with consumerId: update consumer data
              guard: ({ context }) =>
                !!context.consumerId && !!context._returningPatientOutput,
              target: "updatingConsumerData",
              actions: [
                mergeArcOutput<IntakeOutput>("_intakeOutput"),
                assign({ _reviewMode: () => undefined }),
              ],
            },
            {
              target: "loadHrts",
              actions: [
                mergeArcOutput<IntakeOutput>("_intakeOutput"),
                assign({ _reviewMode: () => undefined }),
              ],
            },
          ],
        },
        on: {
          GO_BACK: [
            {
              // Edit/verify flow: BACK from intake → identity (retrace
              // through the edit/verify screens in reverse). Checked
              // BEFORE infoConfirmed so an in-progress edit always
              // retraces through identity, regardless of any stale
              // infoConfirmed state. Both review mode AND verify mode
              // set `_reviewMode: true`; identity arc's route + verifyBack
              // states handle the re-invocation walking based on
              // `_verifyMode` + `_screenHistory`.
              guard: ({ context }) => isReviewMode(context),
              target: "identifying",
              actions: assign(
                // biome-ignore lint/suspicious/noExplicitAny: XState cross-arc signal event typing
                ({ context, event }: { context: any; event: any }) => ({
                  responses: event.responses ?? context.responses,
                }),
              ),
            },
            {
              // Returning patient who confirmed info: BACK from HIPAA/CC auth → info review
              guard: ({ context }) => hasInfoConfirmed(context),
              target: "returningPatient",
              actions: assign(
                // biome-ignore lint/suspicious/noExplicitAny: XState cross-arc signal event typing
                ({ context, event }: { context: any; event: any }) => ({
                  responses: event.responses ?? context.responses,
                }),
              ),
            },
            {
              target: "identifying",
              actions: assign(
                // biome-ignore lint/suspicious/noExplicitAny: XState cross-arc signal event typing
                ({ context, event }: { context: any; event: any }) => ({
                  responses: event.responses ?? context.responses,
                }),
              ),
            },
          ],
        },
      },

      // ===== UPDATE CONSUMER DATA (returning patients only) =====
      updatingConsumerData: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.updating-consumer-data",
        },
        invoke: {
          id: "updateConsumerData",
          src: "updateConsumerData",
          input: ({ context }) => {
            const returningOutput = context._returningPatientOutput as
              | ReturningPatientOutput
              | undefined;
            return {
              consumerId: context.consumerId as string,
              email: (context.email as string) ?? "",
              firstname: (context.firstName as string) ?? "",
              lastname: (context.lastName as string) ?? "",
              dob: (context.dateOfBirth as string) ?? "",
              sex: (context.sex as string) ?? "",
              preferredLanguage: context.language as string | undefined,
              ehrLocationId: context.ehrLocationId as string | undefined,
              intakeFormSections: context.intakeFormSections,
              intakeResponses: context.responses,
              originalData: {
                firstName: returningOutput?.firstName,
                lastName: returningOutput?.lastName,
                dateOfBirth: returningOutput?.dateOfBirth,
                sex: returningOutput?.sex,
              },
            };
          },
          // Actor always resolves (fire-and-forget via Promise.allSettled)
          onDone: [
            {
              guard: "hasSelectedAppointment",
              target: "submittingReturningIntake",
            },
            { target: "loadHrts" },
          ],
          onError: [
            {
              guard: "hasSelectedAppointment",
              target: "submittingReturningIntake",
            },
            { target: "loadHrts" },
          ],
        },
      },

      // ===== SUBMIT INTAKE FORMS (returning patients with appointment) =====
      submittingReturningIntake: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.submitting-intake-forms",
        },
        invoke: {
          id: "submitReturningIntake",
          src: "submitIntakeForms",
          input: ({ context }) => {
            const returningOutput = context._returningPatientOutput as
              | ReturningPatientOutput
              | undefined;
            return buildSubmitIntakeInput(
              context,
              returningOutput?.selectedAppointmentId ?? "",
            );
          },
          onDone: {
            target: "markingAsArrived",
            actions: ["saveActorResult"],
          },
          onError: {
            // Fire-and-forget: proceed to mark as arrived even on failure
            target: "markingAsArrived",
          },
        },
      },

      loadHrts: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.loading-hrts",
        },
        invoke: {
          id: "loadHrts",
          src: "loadHrts",
          input: ({ context }) => ({
            orgId: context.orgId,
          }),
          onDone: [
            {
              // Workers comp + workplace injury both confirmed in triage
              guard: ({ context }) =>
                context.isWorkersComp === true &&
                context.isWorkplaceInjury === true,
              target: "fetchingWorkersCompPricing",
              actions: ["saveActorResult"],
            },
            {
              // Workers comp confirmed but not workplace injury (EPS)
              guard: ({ context }) => context.isWorkersComp === true,
              target: "fetchingEPSPricing",
              actions: ["saveActorResult"],
            },
            {
              // Patient chose not to use insurance in triage → skip to self-pay pricing
              guard: ({ context }) => context.planToUseInsurance === false,
              target: "fetchingDefaultPricing",
              actions: ["saveActorResult"],
            },
            {
              // Patient has travel insurance → use travel insurance pricing flow
              // https://linear.app/healthcarerevolution/issue/PLA-687/kiosk-refactor (Noted gap for travel insurance HRT not live, addressed here)
              guard: ({ context }) => context.isTravelInsurance === true,
              target: "fetchingTravelInsurancePricing",
              actions: ["saveActorResult"],
            },
            {
              // Patient chose to use insurance → insurance input
              guard: ({ context }) => context.planToUseInsurance === true,
              target: "insuring",
              actions: ["saveActorResult"],
            },
            {
              // Fallback: no specific routing matched → self-pay pricing
              target: "fetchingDefaultPricing",
              actions: ["saveActorResult"],
            },
          ],
          onError: {
            target: "insuring",
            actions: ["saveError"],
          },
        },
      },

      // ===== WORKERS COMP PRICING =====
      fetchingWorkersCompPricing: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.fetching-pricing",
        },
        invoke: {
          id: "fetchPricing",
          src: "fetchPricing",
          input: ({ context }) => {
            const workersCompHrt = context.workersCompHrt as {
              hrtId: number;
            } | null;
            const workersCompHrtIds = workersCompHrt
              ? [workersCompHrt.hrtId]
              : [];

            return {
              consumerId: context.consumerId,
              firstName: context.firstName,
              lastName: context.lastName,
              dateOfBirth: context.dateOfBirth,
              insurances: [],
              hrtIds: workersCompHrtIds,
              orgId: context.orgId,
              email: context.email,
              insuranceVerified: false,
              practiceId: Number.parseInt(context.practiceId),
            };
          },
          onDone: {
            // Route to early data write, then to workersCompTreatment after upload
            target: "createUserAndConsumer",
            actions: [
              "saveActorResult",
              assign({
                selectedHrt: ({ context }) => context.workersCompHrt,
                targetTreatmentState: "workersCompTreatment",
                preDataWriteState: "intaking",
              }),
            ],
          },
          onError: {
            // Even on error, route to early data write
            target: "createUserAndConsumer",
            actions: [
              "saveError",
              assign({
                selectedHrt: ({ context }) => context.workersCompHrt,
                targetTreatmentState: "workersCompTreatment",
                preDataWriteState: "intaking",
              }),
            ],
          },
        },
      },

      workersCompTreatment: {
        meta: {
          componentName: "TreatmentPurchase",
          contentKey: "medrite.checkin.workers-comp-treatment",
        },
        on: {
          PAY_NOW: {
            target: "preparePayment",
            actions: assign({
              pricingMode: () => "CASH",
              paymentRoute: "PAY_NOW",
            }),
          },
          PAY_AT_DESK: {
            target: "completeFrontDesk",
            actions: assign({ paymentRoute: "PAY_AT_DESK" }),
          },
          COMPLETE: {
            target: "completeWorkersCompEPSOrTravel",
            actions: assign({ paymentRoute: "COMPLETE" }),
          },
          BACK: { target: "intaking" },
        },
      },

      // ===== EPS PRICING =====
      fetchingEPSPricing: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.fetching-pricing",
        },
        invoke: {
          id: "fetchPricing",
          src: "fetchPricing",
          input: ({ context }) => {
            const epsHrt = context.epsHrt as { hrtId: number } | null;
            const epsHrtIds = epsHrt ? [epsHrt.hrtId] : [];

            return {
              consumerId: context.consumerId,
              firstName: context.firstName,
              lastName: context.lastName,
              dateOfBirth: context.dateOfBirth,
              insurances: [],
              hrtIds: epsHrtIds,
              orgId: context.orgId,
              email: context.email,
              insuranceVerified: false,
              practiceId: Number.parseInt(context.practiceId),
            };
          },
          onDone: {
            // Route to early data write, then to epsTreatment after upload
            target: "createUserAndConsumer",
            actions: [
              "saveActorResult",
              assign({
                selectedHrt: ({ context }) => context.epsHrt,
                targetTreatmentState: "epsTreatment",
                preDataWriteState: "intaking",
              }),
            ],
          },
          onError: {
            // Even on error, route to early data write
            target: "createUserAndConsumer",
            actions: [
              "saveError",
              assign({
                selectedHrt: ({ context }) => context.epsHrt,
                targetTreatmentState: "epsTreatment",
                preDataWriteState: "intaking",
              }),
            ],
          },
        },
      },

      epsTreatment: {
        meta: {
          componentName: "TreatmentPurchase",
          contentKey: "medrite.checkin.eps-treatment",
        },
        on: {
          PAY_NOW: {
            target: "preparePayment",
            actions: assign({
              pricingMode: () => "CASH",
              paymentRoute: "PAY_NOW",
            }),
          },
          PAY_AT_DESK: {
            target: "completeFrontDesk",
            actions: assign({ paymentRoute: "PAY_AT_DESK" }),
          },
          COMPLETE: {
            target: "completeWorkersCompEPSOrTravel",
            actions: assign({ paymentRoute: "COMPLETE" }),
          },
          BACK: { target: "intaking" },
        },
      },

      // ===== TRAVEL INSURANCE PRICING =====
      fetchingTravelInsurancePricing: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.fetching-pricing",
        },
        invoke: {
          id: "fetchPricing",
          src: "fetchPricing",
          input: ({ context }) => {
            const travelInsuranceHrt = context.travelInsuranceHrt as {
              hrtId: number;
            } | null;
            const travelInsuranceHrtIds = travelInsuranceHrt
              ? [travelInsuranceHrt.hrtId]
              : [];
            return {
              consumerId: context.consumerId,
              firstName: context.firstName,
              lastName: context.lastName,
              dateOfBirth: context.dateOfBirth,
              insurances: [],
              hrtIds: travelInsuranceHrtIds,
              orgId: context.orgId,
              email: context.email,
              insuranceVerified: false,
              practiceId: Number.parseInt(context.practiceId),
            };
          },
          onDone: {
            target: "createUserAndConsumer",
            actions: [
              "saveActorResult",
              assign({
                selectedHrt: ({ context }) => context.travelInsuranceHrt,
                targetTreatmentState: "travelInsuranceTreatment",
                preDataWriteState: "intaking",
              }),
            ],
          },
          onError: {
            target: "createUserAndConsumer",
            actions: [
              "saveError",
              assign({
                selectedHrt: ({ context }) => context.travelInsuranceHrt,
                targetTreatmentState: "travelInsuranceTreatment",
                preDataWriteState: "intaking",
              }),
            ],
          },
        },
      },

      travelInsuranceTreatment: {
        meta: {
          componentName: "TreatmentPurchase",
          contentKey: "medrite.checkin.travel-insurance-treatment",
        },
        on: {
          PAY_NOW: {
            target: "preparePayment",
            actions: assign({
              pricingMode: () => "CASH",
              paymentRoute: "PAY_NOW",
            }),
          },
          PAY_AT_DESK: {
            target: "completeFrontDesk",
            actions: assign({ paymentRoute: "PAY_AT_DESK" }),
          },
          COMPLETE: {
            target: "completeWorkersCompEPSOrTravel",
            actions: assign({ paymentRoute: "COMPLETE" }),
          },
          BACK: { target: "intaking" },
        },
      },

      // ===== INSURANCE ARC =====
      insuring: {
        invoke: {
          src: "insuranceArc",
          id: "insuranceArc",
          input: ({ context }) => assembleInsuranceArcInput(context),
          // Always re-fetch pricing via the machine actor on arc completion.
          // For the storefront re-add path (returnToStorefront), addingInsurance-
          // FromStorefront is still true, so fetchingDefaultPricing prices all
          // HRTs and routes back to storefrontBrowse (clearing the flag). For the
          // normal check-in path it prices the default HRT and continues to data
          // write. The storefront's own React Query pricing fetch is not a
          // reliable refresh — it does not commit to the machine pricingMap when
          // the user navigates quickly — so the actor is authoritative.
          onDone: [
            {
              // Insurance provided while a consumer is already persisted
              // (storefront late-add): write the insurance row before
              // repricing. In the normal intake flow createInsurance runs
              // after createUserAndConsumer instead — so this branch is
              // limited to the storefront-add path, otherwise returning
              // patients with a saved consumer could re-enter
              // createInsurance and skip straight to createAppointment
              // without repricing.
              guard: ({ context, event }) => {
                const output = (
                  event as {
                    output?: {
                      insurances?: {
                        primaryInsurance?: unknown;
                        secondaryInsurance?: unknown;
                      };
                    };
                  }
                ).output;
                return (
                  context.addingInsuranceFromStorefront === true &&
                  !!context.consumerId &&
                  !!(
                    output?.insurances?.primaryInsurance ||
                    output?.insurances?.secondaryInsurance
                  )
                );
              },
              target: "createInsurance",
              actions: [
                mergeArcOutput<InsuranceOutput>("_insuranceOutput"),
                assign({
                  addedInsuranceFromStorefront: ({ context }) =>
                    context.addingInsuranceFromStorefront === true,
                }),
              ],
            },
            {
              target: "fetchingDefaultPricing",
              actions: [
                mergeArcOutput<InsuranceOutput>("_insuranceOutput"),
                assign({
                  addedInsuranceFromStorefront: ({ context }) =>
                    context.addingInsuranceFromStorefront === true,
                }),
              ],
            },
          ],
        },
        on: {
          GO_BACK: { target: "intaking" },
          // Signal from InsuranceVerificationFailure "go to front desk" button
          FRONT_DESK: {
            target: "fetchingDefaultPricing",
            // biome-ignore lint/suspicious/noExplicitAny: XState event data typing
            actions: assign(({ event }: any) => {
              const data = event.data || {};
              const failureType = data.failureType || "unverified";
              const isUnverified = failureType === "unverified";
              return {
                // Merge insurance data from arc
                insurances: data.insurances,
                insuranceStates: data.insuranceStates,
                insuranceInputSnapshot: data.insuranceInputSnapshot,
                primaryInsurancePhotoFront: data.primaryInsurancePhotoFront,
                primaryInsurancePhotoBack: data.primaryInsurancePhotoBack,
                secondaryInsurancePhotoFront: data.secondaryInsurancePhotoFront,
                secondaryInsurancePhotoBack: data.secondaryInsurancePhotoBack,
                ocrPrimaryInsurance: data.ocrPrimaryInsurance,
                ocrSecondaryInsurance: data.ocrSecondaryInsurance,
                // Set front desk mode + pricing behavior
                frontDeskMode: true,
                pendingPricesMode: isUnverified,
                pricingMode: isUnverified ? undefined : "CASH",
              };
            }),
          },
        },
      },

      // ===== PRICING FETCH =====
      fetchingDefaultPricing: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.fetching-pricing",
        },
        invoke: {
          id: "fetchPricing",
          src: "fetchPricing",
          input: ({ context }) => {
            console.log(
              "[fetchingDefaultPricing] context before pricing fetch:",
              context,
            );

            // Get default HRT ID from context (loaded by loadHrts actor)
            const defaultHrt = context.defaultHrt as { hrtId: number } | null;
            const defaultHrtIds = defaultHrt ? [defaultHrt.hrtId] : [];

            // If coming from storefront, use all HRTs instead of just default
            const isFromStorefront =
              context.addingInsuranceFromStorefront === true;

            const shouldUseAllHrts = isFromStorefront;

            let hrtIds: number[];
            if (shouldUseAllHrts && context.hrts) {
              // Get all HRT IDs from context (same as KioskEventContext)
              const epsHrt = context.epsHrt as { hrtId: number } | null;
              const workersCompHrt = context.workersCompHrt as {
                hrtId: number;
              } | null;
              const allHrtIds =
                // biome-ignore lint/suspicious/noExplicitAny: HRT type inference
                (context.hrts as any[])?.map((hrt: any) => hrt.hrtId) ?? [];
              // Filter out EPS and Workers Comp HRTs (same as KioskEventContext)
              hrtIds = allHrtIds.filter(
                (id: number) =>
                  id !== epsHrt?.hrtId && id !== workersCompHrt?.hrtId,
              );

              datadogLogs.logger.log(
                "[fetchingDefaultPricing] filtered hrtIds:",
                hrtIds,
              );
            } else {
              // Otherwise, use just the default HRT
              hrtIds = defaultHrtIds;
              datadogLogs.logger.log(
                "[fetchingDefaultPricing] using defaultHrtIds:",
                hrtIds,
              );
            }

            const insurances = context.insurances as {
              primaryInsurance?: {
                payer: string;
                memberId: string;
                state: string;
                error: ErrorCode | null;
                status?: string;
              };
              secondaryInsurance?: {
                payer: string;
                memberId: string;
                error: ErrorCode | null;
                state: string;
                status?: string;
              };
            };

            // Filter to only include active insurances for pricing
            const insurancesArray = [
              insurances?.primaryInsurance,
              insurances?.secondaryInsurance,
            ]
              .filter(
                (
                  insurance,
                  index,
                  arr,
                ): insurance is {
                  payer: string;
                  memberId: string;
                  state: string;
                  status?: string;
                  error: ErrorCode | null;
                } => {
                  if (insurance === undefined || insurance === null)
                    return false;

                  // Only filter out secondary (index 1) if unverified AND we have a primary
                  const isSecondary = index === 1;
                  const isUnverified =
                    insurance.status?.toLowerCase() === "unverified";
                  const hasPrimary = arr[0] !== undefined && arr[0] !== null;

                  if (isSecondary && isUnverified && hasPrimary) {
                    return false;
                  }

                  return true;
                },
              )
              .filter(
                (insurance) =>
                  !insurance.error ||
                  insurance.error !== ErrorCode.OUT_OF_NETWORK,
              );

            // Travel insurance = self-pay only, don't send insurance data
            // (return users may have existing insurance in context that lacks
            // the payerId field required by the pricing actor's Zod schema)
            const isTravelIns = context.isTravelInsurance === true;

            // Self-pay: patient chose "No insurance" in triage — ignore cached
            // insurance data that may still be in context from a previous pass.
            // Exception: insurance just added from the storefront — the triage
            // answer predates it and must not suppress insured pricing.
            const isSelfPay =
              context.planToUseInsurance === false &&
              context.addingInsuranceFromStorefront !== true;

            return {
              consumerId: context.consumerId,
              firstName: context.firstName,
              lastName: context.lastName,
              dateOfBirth: context.dateOfBirth,
              insurances: isTravelIns || isSelfPay ? [] : insurancesArray,
              hrtIds,
              orgId: context.orgId,
              email: context.email,
              insuranceVerified:
                isTravelIns || isSelfPay
                  ? false
                  : isAtLeastPrimaryInsuranceActive(context),
              practiceId: Number.parseInt(context.practiceId),
              isPendingPricesFetch: context.pendingPricesMode || false,
            };
          },
          onDone: [
            {
              // Front desk mode: skip storefront, go straight to data write
              guard: "isFrontDeskMode",
              target: "createUserAndConsumer",
              actions: [
                "saveActorResult",
                assign(({ context }) => ({
                  selectedHrt: context.defaultHrt,
                  pricingInputSnapshot: createPricingInputSnapshot(context),
                })),
              ],
            },
            {
              // If coming from storefront, go directly back to storefront
              guard: ({ context }) =>
                context.addingInsuranceFromStorefront === true,
              target: "storefrontBrowse",
              actions: [
                "saveActorResult",
                assign({
                  addingInsuranceFromStorefront: false,
                  addedInsuranceFromStorefront: true,
                  // Save snapshot of inputs used for this pricing fetch
                  pricingInputSnapshot: ({ context }) =>
                    createPricingInputSnapshot(context),
                }),
              ],
            },
            {
              // Otherwise, go to storefrontBrowse
              target: "storefrontBrowse",
              actions: [
                "saveActorResult",
                // Save snapshot of inputs used for this pricing fetch
                assign({
                  pricingInputSnapshot: ({ context }) =>
                    createPricingInputSnapshot(context),
                }),
              ],
            },
          ],
          onError: [
            {
              // Front desk mode: still try to write user/consumer even without pricing
              guard: "isFrontDeskMode",
              target: "createUserAndConsumer",
              actions: ["saveError"],
            },
            {
              // Pricing failed - continue to browse (can show error in component)
              target: "storefrontBrowse",
              actions: ["saveError"],
            },
          ],
        },
      },
      defaultTreatment: {
        meta: {
          componentName: "TreatmentPurchase",
          contentKey: "medrite.checkin.default-treatment",
        },
        on: {
          PAY_NOW: [
            {
              guard: and(["hasExistingAppointment", "isTreatmentChanged"]),
              target: "replacingBoughtTreatment",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_NOW",
                previousTreatmentState: "defaultTreatment",
              }),
            },
            {
              target: "preparePayment",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_NOW",
                previousTreatmentState: "defaultTreatment",
              }),
            },
          ],
          PAY_AT_DESK: [
            {
              guard: and(["hasExistingAppointment", "isTreatmentChanged"]),
              target: "replacingBoughtTreatment",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_AT_DESK",
                previousTreatmentState: "defaultTreatment",
              }),
            },
            {
              target: "completeFrontDesk",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_AT_DESK",
                previousTreatmentState: "defaultTreatment",
              }),
            },
          ],
          PAY_LATER: [
            {
              guard: and(["hasExistingAppointment", "isTreatmentChanged"]),
              target: "replacingBoughtTreatment",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_LATER",
                previousTreatmentState: "defaultTreatment",
              }),
            },
            {
              target: "preparePayment",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_LATER",
                previousTreatmentState: "defaultTreatment",
              }),
            },
          ],
          COMPLETE: {
            target: "completeDefault",
            actions: assign({
              paymentRoute: "COMPLETE",
              previousTreatmentState: "defaultTreatment",
            }),
          },
          SET_PRICING_MAP: {
            actions: [
              assign(({ context, event }) => {
                console.log("SET_PRICING_MAP", event);
                const eventData = event.data as { newPricingMap?: unknown };
                const existingPricingMap =
                  (context.pricingMap as Record<string, unknown>) ?? {};
                const newPricingMap =
                  (eventData.newPricingMap as Record<string, unknown>) ?? {};

                // Merge existing pricing (defaultHrt) with new pricing (storefront)
                return {
                  ...context,
                  pricingMap: {
                    ...existingPricingMap,
                    ...newPricingMap,
                  },
                };
              }),
            ],
          },
          SET_PRICING_MODE: {
            actions: [
              assign(({ context, event }) => {
                const { hrtId, mode } = event.data as {
                  hrtId: string;
                  mode: "CASH" | "INSURED";
                };
                const existingPricingMap =
                  (context.pricingMap as Record<string, unknown>) ?? {};
                const existingHrtPricing = existingPricingMap[hrtId] as
                  | Record<string, unknown>
                  | undefined;

                return {
                  pricingMap: {
                    ...existingPricingMap,
                    [hrtId]: {
                      ...existingHrtPricing,
                      mode,
                    },
                  },
                };
              }),
            ],
          },
          BACK: { target: "storefrontBrowse" },
        },
      },

      createUserAndConsumer: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.creating-account",
        },
        invoke: {
          id: "createUserAndConsumer",
          src: "createUserAndConsumer",
          input: ({ context }) => ({
            firstname: context.firstName,
            lastname: context.lastName,
            phone: context.phoneNumber || "",
            email: context.email || "",
            orgId: context.orgId,
            dob: context.dateOfBirth || "",
            preferredLanguage: context.language || "en",
            ehrLocationId: context.ehrLocationId || "",
            stripeCustomerId: context.stripeCustomerId,
            sex: context.sex,
            intakeResponses: context.responses,
            intakeFormSections: context.intakeFormSections,
            // Pass existing IDs to skip creation if already exists
            userId: context.userId,
            consumerId: context.consumerId,
            userIdBackground: context.userIdBackground,
            consumerIdBackground: context.consumerIdBackground,
          }),
          onDone: [
            {
              // If workers comp, skip to appointment creation
              guard: ({ context }) => context.isWorkersComp === true,
              target: "createAppointmentWithBoughtTreatments",
              actions: ["saveActorResult"],
            },
            {
              // If has primary insurance, create insurance record
              guard: "hasInsuranceInContext",
              target: "createInsurance",
              actions: ["saveActorResult"],
            },
            {
              // Otherwise, skip to appointment creation
              target: "createAppointmentWithBoughtTreatments",
              actions: ["saveActorResult"],
            },
          ],
          onError: {
            target: "userCreationError",
            actions: ["saveError"],
          },
        },
      },

      createInsurance: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.creating-insurance",
        },
        always: [
          {
            // Skip insurance creation if insuranceId already exists
            // AND appointment already exists (late insurance flow)
            // AND insurance input has NOT changed → go to pricing
            guard: and([
              ({ context }) => {
                const insurances = context.insurances as {
                  primaryInsurance?: { insuranceId?: string };
                  secondaryInsurance?: { insuranceId?: string };
                };

                const hasPrimary = !!insurances?.primaryInsurance;
                const hasSecondary = !!insurances?.secondaryInsurance;
                const primaryHasId =
                  !!insurances?.primaryInsurance?.insuranceId;
                const secondaryHasId =
                  !!insurances?.secondaryInsurance?.insuranceId;

                const doesAllInsuranceHaveId = hasSecondary
                  ? primaryHasId && secondaryHasId
                  : hasPrimary && primaryHasId;

                return doesAllInsuranceHaveId;
              },
              "hasExistingAppointment",
            ]),
            target: "fetchingDefaultPricing",
          },
          // Guard 2 removed (PLA-1213): the actor now handles skip-or-update-or-create
          // internally. When all insurances have IDs and data hasn't changed, the actor
          // returns existing IDs immediately. When data changed, it calls update.
        ],
        invoke: {
          id: "createInsurance",
          src: "createInsurance",
          input: ({ context }) => {
            const insurances = context.insurances as {
              primaryInsurance?: {
                payerId: string;
                payer: string;
                memberId: string;
                state: string;
                status?: string;
                ehrInsuranceId?: string;
                ehrInsurancePackageId?: string;
                coverageOverview?: {
                  groupNumber: string;
                  planName?: string;
                };
                planName?: string;
                subscriberInfo: {
                  subscriberDob: string;
                  subscriberSex: string;
                  subscriberFirstName: string;
                  subscriberLastName: string;
                  relationshipToInsured: string;
                  relationshipToInsuredCode: string;
                };
              };
              secondaryInsurance?: {
                payer: string;
                payerId: string;
                groupNumber: string;
                memberId: string;
                state: string;
                status?: string;
                ehrInsuranceId?: string;
                ehrInsurancePackageId?: string;
                coverageOverview?: {
                  groupNumber: string;
                  planName?: string;
                };

                subscriberInfo: {
                  subscriberDob: string;
                  subscriberSex: string;
                  subscriberFirstName: string;
                  subscriberLastName: string;
                  relationshipToInsured: string;
                  relationshipToInsuredCode: string;
                };
              };
            };

            return {
              primaryInsurance: insurances?.primaryInsurance,
              secondaryInsurance: insurances?.secondaryInsurance,
              consumerId: context.consumerId as string,
              orgId: context.orgId as number,
              // Background insurance fields for duplicate detection (from skipped OTP flow)
              primaryInsuranceBackground: (
                context as { primaryInsuranceBackground?: unknown }
              ).primaryInsuranceBackground,
              secondaryInsuranceBackground: (
                context as { secondaryInsuranceBackground?: unknown }
              ).secondaryInsuranceBackground,
              // Flag: insurance input changed since last verification → actor should update existing records
              insuranceInputChanged: hasInsuranceInputChanged(context),
            };
          },
          onDone: [
            {
              // Late insurance from storefront: go back to pricing → storefront
              guard: ({ context }) =>
                context.addingInsuranceFromStorefront === true,
              target: "fetchingDefaultPricing",
              actions: ["saveInsuranceIds"],
            },
            {
              // Late insurance flow (non-storefront): appointment exists, go to pricing
              guard: "hasExistingAppointment",
              target: "fetchingDefaultPricing",
              actions: ["saveInsuranceIds"],
            },
            {
              // Normal flow: create appointment next
              target: "createAppointmentWithBoughtTreatments",
              actions: ["saveInsuranceIds"],
            },
          ],
          onError: {
            target: "insuranceCreationError",
            actions: ["saveError"],
          },
        },
      },

      insuranceCreationError: {
        meta: {
          componentName: "FrontDeskErrorScreen",
          contentKey: "medrite.checkin.write-failure",
        },
      },

      createAppointmentWithBoughtTreatments: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.creating-appointment",
        },
        always: [
          {
            // Front desk mode + pricing fetch failed: skip appointment creation
            // (user/consumer/insurance already written, but no pricing data to create appointment)
            guard: ({ context }) =>
              context.frontDeskMode === true &&
              (!context.pricingMap ||
                Object.keys(context.pricingMap as object).length === 0),
            target: "submittingIntakeForms",
          },
          {
            // If appointment exists but treatment changed (e.g. workers' comp → EPS
            // after user pressed BACK), replace the bought treatment
            guard: and(["hasExistingAppointment", "isTreatmentChanged"]),
            target: "replacingBoughtTreatment",
          },
          {
            // Skip to completePayment if appointment exists and PAY_AT_DESK
            guard: ({ context }) =>
              !!context.appointment &&
              (context as { paymentRoute?: string }).paymentRoute ===
                "PAY_AT_DESK",
            target: "completeFrontDesk",
          },
          {
            // Skip to completePayment if appointment exists and PAY_AT_DESK
            guard: ({ context }) =>
              !!context.appointment &&
              (context as { paymentRoute?: string }).paymentRoute ===
                "PAY_LATER",
            target: "preparePayment",
          },
          {
            // Skip to completeWorkersCompEPSOrTravel if appointment exists and COMPLETE for WC/EPS/Travel
            guard: ({ context }) =>
              !!context.appointment &&
              (context as { paymentRoute?: string }).paymentRoute ===
                "COMPLETE" &&
              (context.isWorkersComp === true ||
                context.isTravelInsurance === true),
            target: "completeWorkersCompEPSOrTravel",
          },
          {
            // Skip to completeDefault if appointment exists and COMPLETE
            guard: ({ context }) =>
              !!context.appointment &&
              (context as { paymentRoute?: string }).paymentRoute ===
                "COMPLETE",
            target: "completeDefault",
          },
          {
            // Route to treatment state if appointment exists (default)
            // Handles re-traversal (BACK from treatment → intake → forward):
            // routeToTreatmentState checks paymentRoute first (handles PAY_NOW),
            // then falls through to treatment state guards (no-payment re-traversal)
            guard: ({ context }) => !!context.appointment,
            target: "routeToTreatmentState",
          },
        ],

        invoke: {
          id: "createAppointmentWithBoughtTreatments",
          src: "createAppointmentWithBoughtTreatments",
          input: ({ context }) => {
            const hrt = context.selectedHrt || context.defaultHrt;
            const submitted = (context.submittedFormIds || []) as string[];
            const intakeFormIds = submitted.map(Number);
            return {
              orgId: context.orgId,
              practiceId: Number(context.practiceId),
              consumerId: context.consumerId,
              hrt,
              pricingMap: context.pricingMap,
              pricingMode: context.pricingMode as
                | "CASH"
                | "INSURED"
                | undefined,
              pendingPricesMode: context.pendingPricesMode,
              intakeFormIds,
            };
          },
          onDone: {
            target: "submittingIntakeForms",
            actions: ["saveActorResult"],
          },
          onError: {
            target: "appointmentCreationError",
            actions: ["saveError"],
          },
        },
      },

      appointmentCreationError: {
        meta: {
          componentName: "FrontDeskErrorScreen",
          contentKey: "medrite.checkin.write-failure",
        },
      },

      /**
       * Replace Bought Treatment State
       *
       * Handles the case where user goes back and selects a different treatment
       * after an appointment has already been created. Deletes the old bought
       * treatment and creates a new one with the new HRT.
       */
      replacingBoughtTreatment: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.uploading-patient-data",
        },
        invoke: {
          id: "replaceBoughtTreatment",
          src: "replaceBoughtTreatment",
          input: ({ context }) => {
            // Use helpers to extract data from context
            const appointmentData = extractAppointmentData(context);
            const hrt = getEffectiveHrt(context);
            const pricing = extractPricingData(
              context.pricingMap,
              hrt?.hrtId,
              context.pricingMode as string | undefined,
            );
            const insuranceIds = extractInsuranceIds(context, pricing.mode);

            return {
              boughtTreatmentIds: appointmentData.boughtTreatmentIds,
              consumerId: context.consumerId as string,
              pricedTreatmentId: pricing.pricedTreatmentId,
              price: pricing.price,
              paymentType: pricing.paymentType,
              insuranceIds: insuranceIds.length > 0 ? insuranceIds : undefined,
              slotId: appointmentData.slotId ?? "",
              orgId: context.orgId as number,
              treatmentName: hrt?.name ?? "Treatment",
              hrtId: hrt?.hrtId ?? 0,
              isVirtual: false,
              reviewStatus:
                context.pendingPricesMode && pricing.mode === "INSURED"
                  ? "NEEDS_ADMIN_CREATED"
                  : undefined,
            };
          },
          onDone: {
            actions: [
              // Update context with new bought treatment info
              assign(({ context, event }) => {
                const result = event.output as {
                  newBoughtTreatment: { id: string; hrtId: number };
                };
                // Update the appointment context with new bought treatment
                const existingAppointment = context.appointment as {
                  data?: {
                    boughtTreatmentWithCharges?: Array<{
                      id: string;
                      hrtId: number;
                    }>;
                  };
                };
                return {
                  appointment: {
                    ...existingAppointment,
                    data: {
                      ...existingAppointment?.data,
                      boughtTreatmentWithCharges: [result.newBoughtTreatment],
                    },
                  },
                };
              }),
            ],
            target: "routeToTreatmentState",
          },
          onError: {
            target: "replaceBoughtTreatmentError",
            actions: ["saveError"],
          },
        },
      },

      replaceBoughtTreatmentError: {
        meta: {
          componentName: "FrontDeskErrorScreen",
          contentKey: "medrite.checkin.write-failure",
        },
      },

      /**
       * Route to Treatment State
       *
       * Intermediate state that routes based on context:
       * - If paymentRoute is set (coming from PAY event on treatment screen
       *   after pricing mode change), routes to the payment flow.
       * - Otherwise routes to the correct treatment state based on
       *   targetTreatmentState context value.
       */
      routeToTreatmentState: {
        always: [
          // If paymentRoute is set, continue to payment flow
          // (replacement was triggered from a PAY event on the treatment screen)
          {
            guard: ({ context }) =>
              (context as { paymentRoute?: string }).paymentRoute === "PAY_NOW",
            target: "preparePayment",
          },
          {
            guard: ({ context }) =>
              (context as { paymentRoute?: string }).paymentRoute ===
              "PAY_AT_DESK",
            target: "completeFrontDesk",
          },
          {
            guard: ({ context }) =>
              (context as { paymentRoute?: string }).paymentRoute ===
              "PAY_LATER",
            target: "preparePayment",
          },
          {
            guard: ({ context }) =>
              (context as { paymentRoute?: string }).paymentRoute ===
              "COMPLETE",
            target: "completeDefault",
          },
          // Otherwise, route to the treatment state (storefront flow)
          {
            guard: "targetIsDefaultTreatment",
            target: "defaultTreatment",
          },
          {
            guard: "targetIsSpecificTreatment",
            target: "specificTreatment",
          },
          {
            guard: "targetIsWorkersCompTreatment",
            target: "workersCompTreatment",
          },
          {
            guard: "targetIsEpsTreatment",
            target: "epsTreatment",
          },
          {
            guard: "targetIsTravelInsuranceTreatment",
            target: "travelInsuranceTreatment",
          },
          {
            // Default fallback
            target: "defaultTreatment",
          },
        ],
      },

      userCreationError: {
        meta: {
          componentName: "FrontDeskErrorScreen",
          contentKey: "medrite.checkin.write-failure",
        },
      },

      submittingIntakeForms: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.submitting-intake-forms",
        },
        always: [
          {
            // Front desk mode + no appointment: skip intake submission
            // (intake requires appointmentId which doesn't exist when pricing fetch failed)
            guard: ({ context }) =>
              context.frontDeskMode === true &&
              !context.appointmentId &&
              !(
                context.appointment as {
                  data?: { appointment?: { id?: string } };
                }
              )?.data?.appointment?.id,
            target: "uploadingPhotos",
          },
        ],
        invoke: {
          id: "submitIntakeForms",
          src: "submitIntakeForms",
          input: ({ context }) =>
            buildSubmitIntakeInput(
              context,
              (context.appointmentId as string) ??
                (
                  context.appointment as {
                    data: { appointment: { id: string } };
                  }
                )?.data?.appointment?.id,
            ),
          onDone: {
            target: "uploadingPhotos",
            actions: ["saveActorResult"],
          },
          onError: {
            target: "intakeSubmissionError",
            actions: ["saveError"],
          },
        },
      },

      uploadingPhotos: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.uploading-photos",
        },
        invoke: {
          id: "uploadConsumerPhotos",
          src: "uploadConsumerPhotos",
          input: ({ context }) => {
            const isWorkersComp = context.isWorkersComp as boolean | undefined;

            const insuranceArray = buildInsuranceArrayForPhotoUpload(
              context.insurances as Parameters<
                typeof buildInsuranceArrayForPhotoUpload
              >[0],
              !!isWorkersComp,
            );

            return {
              consumerId: context.consumerId as string,
              orgId: context.orgId as number,
              process_insurance_photos: true,
              passportPhoto: context.passportPhoto as string | undefined,
              photos: {
                licensePhotoFront: context.licensePhotoFront as
                  | string
                  | undefined,
                licensePhotoBack: context.licensePhotoBack as
                  | string
                  | undefined,
                primaryInsurancePhotoFront:
                  context.primaryInsurancePhotoFront as string | undefined,
                primaryInsurancePhotoBack: context.primaryInsurancePhotoBack as
                  | string
                  | undefined,
                secondaryInsurancePhotoFront:
                  context.secondaryInsurancePhotoFront as string | undefined,
                secondaryInsurancePhotoBack:
                  context.secondaryInsurancePhotoBack as string | undefined,
              },
              // OCR data for expiration date tracking (used for photo deduplication)
              ocrData: {
                ocrId: context.ocrData as Record<string, unknown> | undefined,
                ocrPrimaryInsurance: context.ocrPrimaryInsurance as
                  | Record<string, unknown>
                  | undefined,
                ocrSecondaryInsurance: context.ocrSecondaryInsurance as
                  | Record<string, unknown>
                  | undefined,
              },
              // Existing photos from setup-photos actor (for expiration checking)
              existingPhotos: context.existingPhotos as
                | Array<{
                    id: string;
                    type: string;
                    ocrMetadata?: Record<string, unknown> | null;
                    linkedInsuranceId?: string | null;
                    associatedPhotoId?: string | null;
                    insurancePriority?: number | null;
                  }>
                | undefined,
              insurances:
                insuranceArray.length > 0 ? insuranceArray : undefined,
            };
          },
          onDone: [
            {
              // Front desk mode: write pipeline complete, go to front desk screen
              guard: "isFrontDeskMode",
              target: "completeFrontDesk",
              actions: [
                "saveActorResult",
                assign({ frontDeskMode: undefined }),
              ],
            },
            // === EARLY DATA WRITE ROUTING (paymentRoute not set yet) ===
            {
              // Route to defaultTreatment if early data write with that target
              guard: and([
                "isDataWriteBeforePayment",
                "targetIsDefaultTreatment",
              ]),
              target: "defaultTreatment",
              actions: ["saveActorResult"],
            },
            {
              // Route to specificTreatment if early data write with that target
              guard: and([
                "isDataWriteBeforePayment",
                "targetIsSpecificTreatment",
              ]),
              target: "specificTreatment",
              actions: ["saveActorResult"],
            },
            {
              // Route to workersCompTreatment if early data write with that target
              guard: and([
                "isDataWriteBeforePayment",
                "targetIsWorkersCompTreatment",
              ]),
              target: "workersCompTreatment",
              actions: ["saveActorResult"],
            },
            {
              // Route to epsTreatment if early data write with that target
              guard: and(["isDataWriteBeforePayment", "targetIsEpsTreatment"]),
              target: "epsTreatment",
              actions: ["saveActorResult"],
            },
            {
              // Route to travelInsuranceTreatment if early data write with that target
              guard: and([
                "isDataWriteBeforePayment",
                "targetIsTravelInsuranceTreatment",
              ]),
              target: "travelInsuranceTreatment",
              actions: ["saveActorResult"],
            },
            // === POST-PAY ROUTING (paymentRoute is set) ===
            {
              // Route to completePayment if PAY_AT_DESK
              guard: ({ context }) =>
                (context as { paymentRoute?: string }).paymentRoute ===
                "PAY_AT_DESK",
              target: "completeFrontDesk",
              actions: ["saveActorResult"],
            },
            {
              // Route to preparePayment if PAY_LATER
              guard: ({ context }) =>
                (context as { paymentRoute?: string }).paymentRoute ===
                "PAY_LATER",
              target: "preparePayment",
              actions: ["saveActorResult"],
            },
            {
              // Skip to completeWorkersCompEPSOrTravel if appointment exists and COMPLETE for WC/EPS/Travel
              guard: ({ context }) =>
                !!context.appointment &&
                (context as { paymentRoute?: string }).paymentRoute ===
                  "COMPLETE" &&
                (context.isWorkersComp === true ||
                  context.isTravelInsurance === true),
              target: "completeWorkersCompEPSOrTravel",
            },
            {
              // Route to completeDefault if COMPLETE
              guard: ({ context }) =>
                (context as { paymentRoute?: string }).paymentRoute ===
                "COMPLETE",
              target: "completeDefault",
              actions: ["saveActorResult"],
            },
            {
              // PAY_NOW + Kiosk Mode - use POS payment (via preparePayment)
              target: "preparePayment",
              actions: ["saveActorResult"],
            },
          ],
          onError: {
            target: "photoUploadError",
            actions: ["saveError"],
          },
        },
      },

      photoUploadError: {
        meta: {
          componentName: "FrontDeskErrorScreen",
          contentKey: "medrite.checkin.write-failure",
        },
      },

      intakeSubmissionError: {
        meta: {
          componentName: "ErrorDisplay",
          contentKey: "medrite.checkin.intake-submission-error",
        },
        on: {
          RETRY: { target: "submittingIntakeForms" },
          SKIP: [
            {
              // Front desk mode: skip intake, still try photos
              guard: "isFrontDeskMode",
              target: "uploadingPhotos",
            },
            // === EARLY DATA WRITE ROUTING (paymentRoute not set yet) ===
            {
              guard: and([
                "isDataWriteBeforePayment",
                "targetIsDefaultTreatment",
              ]),
              target: "defaultTreatment",
            },
            {
              guard: and([
                "isDataWriteBeforePayment",
                "targetIsSpecificTreatment",
              ]),
              target: "specificTreatment",
            },
            {
              guard: and([
                "isDataWriteBeforePayment",
                "targetIsWorkersCompTreatment",
              ]),
              target: "workersCompTreatment",
            },
            {
              guard: and(["isDataWriteBeforePayment", "targetIsEpsTreatment"]),
              target: "epsTreatment",
            },
            {
              guard: and([
                "isDataWriteBeforePayment",
                "targetIsTravelInsuranceTreatment",
              ]),
              target: "travelInsuranceTreatment",
            },
            // === POST-PAY ROUTING ===
            {
              guard: "isQRMode",
              target: "stripeElementPayment",
            },
            {
              target: "preparePayment",
            },
          ],
          BACK: {
            target: "storefrontBrowse",
            actions: assign({ frontDeskMode: undefined }),
          },
        },
      },

      preparePayment: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.preparing-payment",
        },
        entry: "calculatePaymentAmount",
        always: [
          {
            guard: ({ context }) =>
              context.isWorkersComp === true ||
              context.isTravelInsurance === true,
            target: "completeWorkersCompEPSOrTravel",
          },
          {
            // MFA'd user with setup intent flow - check for saved card first
            guard: "shouldCheckSavedCard",
            target: "checkingSavedCard",
          },
          {
            guard: "requiresSetupIntent",
            target: "creatingSetupIntent",
          },
          {
            target: "creatingPaymentIntent",
          },
        ],
      },

      // Check if MFA'd user has a saved payment method
      checkingSavedCard: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.preparing-payment",
        },
        invoke: {
          id: "fetchSavedPaymentMethod",
          src: "fetchSavedPaymentMethod",
          input: ({ context }) => ({
            consumerId: context.consumerId as string,
          }),
          onDone: [
            {
              // Has saved card - show prompt
              guard: ({ event }) => event.output?.paymentMethod != null,
              target: "savedCardPrompt",
              actions: assign({
                savedPaymentMethod: ({ event }) =>
                  (
                    event.output as {
                      paymentMethod: {
                        id: string;
                        brand: string;
                        last4: string;
                        expMonth: number;
                        expYear: number;
                      } | null;
                    }
                  ).paymentMethod,
              }),
            },
            {
              // No saved card - setup intent flow
              guard: "requiresSetupIntent",
              target: "creatingSetupIntent",
            },
            {
              // No saved card - payment intent flow
              target: "creatingPaymentIntent",
            },
          ],
          onError: [
            {
              // On error, fallback to setup intent flow
              guard: "requiresSetupIntent",
              target: "creatingSetupIntent",
            },
            {
              // On error, fallback to payment intent flow
              target: "creatingPaymentIntent",
            },
          ],
        },
      },

      // Prompt user to use saved card or add new one
      savedCardPrompt: {
        meta: {
          componentName: "SavedCardPrompt",
          contentKey: "medrite.checkin.saved-card-prompt",
        },
        on: {
          USE_SAVED_CARD: [
            {
              // Setup intent flow - card already saved, nothing more to do.
              // No new SetupIntent here: clear any stale one from an earlier
              // attempt so completeCardOnFile does not record consent for it.
              guard: "requiresSetupIntent",
              target: "completeCardOnFile",
              actions: assign({ setupIntentId: () => undefined }),
            },
            {
              // Payment intent flow - charge the saved card
              target: "processingCardOnFilePayment",
            },
          ],
          USE_NEW_CARD: [
            {
              // User wants a new card - setup intent flow ($0 or pay later)
              guard: "requiresSetupIntent",
              target: "creatingSetupIntent",
              actions: assign({
                // Clear saved payment method so we don't confuse the flow
                savedPaymentMethod: () => null,
              }),
            },
            {
              // User wants a new card - payment intent flow (pay now)
              target: "creatingPaymentIntent",
              actions: assign({
                // Clear saved payment method so we don't confuse the flow
                savedPaymentMethod: () => null,
              }),
            },
          ],
          BACK: [
            {
              guard: ({ context }) =>
                (context as { previousTreatmentState?: string })
                  .previousTreatmentState === "specificTreatment",
              target: "#medrite-checkin.specificTreatment",
              actions: assign({
                savedPaymentMethod: () => null,
              }),
            },
            {
              guard: ({ context }) =>
                (context as { previousTreatmentState?: string })
                  .previousTreatmentState === "defaultTreatment",
              target: "#medrite-checkin.defaultTreatment",
              actions: assign({
                savedPaymentMethod: () => null,
              }),
            },
            {
              // Fallback to storefrontBrowse if no previous treatment state
              target: "#medrite-checkin.storefrontBrowse",
              actions: assign({
                savedPaymentMethod: () => null,
              }),
            },
          ],
        },
      },

      // Process payment using saved card on file
      processingCardOnFilePayment: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.processing-payment",
        },
        invoke: {
          id: "processCardOnFilePayment",
          src: "processCardOnFilePayment",
          input: ({ context }) => {
            const price = (context.currentPrice as number) || 0;
            const appointmentBtIds =
              context.appointment.data.boughtTreatmentWithCharges.map(
                (bt: { id: string }) => bt.id,
              );
            const savedPM = context.savedPaymentMethod as {
              id: string;
            } | null;

            return {
              amount: Math.round(price * 100),
              savedPaymentMethodId: savedPM?.id || "",
              user: {
                id: context.userId as string,
                fname: context.firstName as string,
                lname: context.lastName as string,
              },
              orgName: context.orgName as string,
              consumer: context.consumerId
                ? {
                    id: context.consumerId as string,
                    stripeCustomerId: undefined,
                  }
                : undefined,
              creationSource: "kiosk",
              appointmentBtIds: appointmentBtIds,
              nonAppointmentBtIds: [],
              collectedBy: "kiosk",
            };
          },
          onDone: {
            target: "completeDefault",
            actions: assign({
              paymentIntentId: ({ event }) =>
                (event.output as { paymentIntentId: string }).paymentIntentId,
              paymentStatus: () => "success",
            }),
          },
          onError: {
            target: "defaultTreatment",
            actions: ["saveError"],
          },
        },
      },

      creatingPaymentIntent: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.creating-payment-intent",
        },
        invoke: {
          id: "createPaymentIntent",
          src: "createPaymentIntent",
          input: ({ context }) => {
            // Use the already-calculated currentPrice from calculatePaymentAmount action
            // This ensures we use the correct pricingMode set by PAY_NOW
            const price = context.currentPrice as number;

            const appointmentBtIds =
              context.appointment.data.boughtTreatmentWithCharges.map(
                (bt: { id: string }) => bt.id,
              );

            // Determine payment method type based on mode, reader availability, and POS health
            // QR mode or no reader or POS unavailable = web form (card), otherwise POS terminal (card_present)
            const isQRMode = context.qrMode === true;
            const hasReader = !!(
              context.stripeReaderId as string | undefined
            )?.trim();
            const isPOSAvailable =
              context.posHealthStatus === "healthy" ||
              context.posHealthStatus === "degraded";
            const useCardPresent = !isQRMode && hasReader && isPOSAvailable;

            return {
              amount: Math.round(price * 100), // Amount in cents
              user: {
                id: context.userId as string,
                fname: context.firstName as string,
                lname: context.lastName as string,
              },
              orgName: context.orgName as string,
              consumer: context.consumerId
                ? {
                    id: context.consumerId as string,
                    stripeCustomerId: undefined,
                  }
                : undefined,
              creationSource: "kiosk",
              paymentMethodType: useCardPresent ? "card_present" : "card",
              collectedBy: "kiosk",
              appointmentBtIds: appointmentBtIds,
              nonAppointmentBtIds: [],
            };
          },
          onDone: [
            {
              // QR mode always uses web form
              guard: "isQRMode",
              target: "stripeElementPayment",
              actions: assign({
                paymentIntentId: ({ event }) =>
                  (event.output as { paymentIntentId: string }).paymentIntentId,
                clientSecret: ({ event }) =>
                  (event.output as { clientSecret: string }).clientSecret,
                customerOptions: ({ event }) =>
                  (
                    event.output as {
                      customerOptions?: {
                        customer: string;
                        ephemeralKey: string;
                      };
                    }
                  ).customerOptions,
              }),
            },
            {
              // POS available - use terminal
              target: "posPayment",
              actions: assign({
                paymentIntentId: ({ event }) =>
                  (event.output as { paymentIntentId: string }).paymentIntentId,
                clientSecret: ({ event }) =>
                  (event.output as { clientSecret: string }).clientSecret,
              }),
            },
          ],
          onError: {
            target: "defaultTreatment",
            actions: ["saveError"],
          },
        },
      },

      creatingSetupIntent: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.creating-payment-intent",
        },
        invoke: {
          id: "createSetupIntent",
          src: "createSetupIntent",
          input: ({ context }) => ({
            user: {
              id: context.userId as string,
              fname: context.firstName as string,
              lname: context.lastName as string,
            },
            consumer: context.consumerId
              ? {
                  id: context.consumerId as string,
                  stripeCustomerId: undefined,
                }
              : undefined,
            creationSource: "kiosk",
            // QR mode, no reader, or POS unavailable = web form (card), otherwise POS terminal (card_present)
            cardPresent:
              context.qrMode !== true &&
              !!(context.stripeReaderId as string | undefined)?.trim() &&
              (context.posHealthStatus === "healthy" ||
                context.posHealthStatus === "degraded"),
          }),
          onDone: [
            {
              // QR mode always uses web form
              guard: "isQRMode",
              target: "stripeElementPayment",
              actions: assign({
                setupIntentId: ({ event }) =>
                  (event.output as { setupIntentId: string }).setupIntentId,
                clientSecret: ({ event }) =>
                  (event.output as { clientSecret: string }).clientSecret,
              }),
            },
            {
              // POS unavailable - fallback to web form with warning
              guard: "shouldFallbackToManualEntry",
              target: "stripeElementPayment",
              actions: [
                "logPOSHealthWarning",
                assign({
                  setupIntentId: ({ event }) =>
                    (event.output as { setupIntentId: string }).setupIntentId,
                  clientSecret: ({ event }) =>
                    (event.output as { clientSecret: string }).clientSecret,
                  // Flag to show fallback UI message
                  posPaymentFallback: () => true,
                }),
              ],
            },
            {
              // POS available - use terminal
              target: "posPayment",
              actions: assign({
                setupIntentId: ({ event }) =>
                  (event.output as { setupIntentId: string }).setupIntentId,
                clientSecret: ({ event }) =>
                  (event.output as { clientSecret: string }).clientSecret,
              }),
            },
          ],
          onError: {
            target: "defaultTreatment",
            actions: ["saveError"],
          },
        },
      },

      posPayment: {
        meta: {
          componentName: "POSPayment",
          contentKey: "medrite.checkin.pos-payment",
        },
        initial: "processingPayment",
        always: [
          {
            // Redirect to Stripe Element Payment if no reader ID provided or POS is offline
            guard: ({ context }) => {
              const stripeReaderId = context.stripeReaderId as
                | string
                | undefined;
              const noReader =
                !stripeReaderId || stripeReaderId.trim().length === 0;
              const posOffline =
                context.posHealthStatus === "offline" ||
                context.posHealthStatus === "unknown";
              return noReader || posOffline;
            },
            target: "stripeElementPayment",
            actions: [
              assign({
                posPaymentFallback: ({ context }) =>
                  context.posHealthStatus === "offline" ||
                  context.posHealthStatus === "unknown",
              }),
            ],
          },
        ],
        states: {
          processingPayment: {
            meta: {
              componentName: "POSPayment",
              contentKey: "medrite.checkin.pos-payment",
            },
            invoke: {
              id: "processPOSPayment",
              src: "processPOSPayment",
              input: ({ context }) => ({
                paymentIntentId: context.paymentIntentId as string | undefined,
                setupIntentId: context.setupIntentId as string | undefined,
                readerId: context.stripeReaderId as string,
                consumerId: context.consumerId as string | undefined,
                pollInterval: 1000,
                timeout: 120000,
              }),
              onDone: {
                target: "paymentSuccess",
                actions: assign({
                  paymentStatus: () => "success",
                  reader: ({ event }) =>
                    (event.output as { reader: unknown }).reader,
                }),
              },
              onError: {
                target: "failed",
                actions: assign({
                  paymentStatus: () => "failure",
                  posError: ({ event }) => event.error,
                }),
              },
            },
            on: {
              BACK: [
                {
                  guard: ({ context }) =>
                    (context as { previousTreatmentState?: string })
                      .previousTreatmentState === "specificTreatment",
                  target: "#medrite-checkin.specificTreatment",
                  actions: assign({
                    paymentStatus: undefined,
                    posError: undefined,
                    paymentIntentId: undefined,
                    setupIntentId: undefined,
                    clientSecret: undefined,
                  }),
                },
                {
                  guard: ({ context }) =>
                    (context as { previousTreatmentState?: string })
                      .previousTreatmentState === "defaultTreatment",
                  target: "#medrite-checkin.defaultTreatment",
                  actions: assign({
                    paymentStatus: undefined,
                    posError: undefined,
                    paymentIntentId: undefined,
                    setupIntentId: undefined,
                    clientSecret: undefined,
                  }),
                },
                {
                  target: "#medrite-checkin.storefrontBrowse",
                  actions: assign({
                    paymentStatus: undefined,
                    posError: undefined,
                    paymentIntentId: undefined,
                    setupIntentId: undefined,
                    clientSecret: undefined,
                  }),
                },
              ],
              GO_TO_FRONT_DESK: {
                target: "#medrite-checkin.completeGoToFrontDesk",
                actions: assign({
                  paymentStatus: undefined,
                  posError: undefined,
                  paymentIntentId: undefined,
                  setupIntentId: undefined,
                  clientSecret: undefined,
                  requiresSetupIntent: undefined,
                }),
              },
            },
          },
          paymentSuccess: {
            meta: {
              componentName: "POSPayment",
              contentKey: "medrite.checkin.pos-payment",
            },
            after: {
              3000: [
                {
                  guard: ({ context }) =>
                    (context as { requiresSetupIntent?: boolean })
                      .requiresSetupIntent === true,
                  target: "#medrite-checkin.completeCardOnFile",
                },
                {
                  target: "#medrite-checkin.completeDefault",
                },
              ],
            },
          },
          failed: {
            entry: logFlowCompleteAction("failed"),
            meta: {
              componentName: "FrontDeskErrorScreen",
              contentKey: "medrite.checkin.write-failure",
            },
          },
        },
      },

      stripeElementPayment: {
        meta: {
          componentName: "StripeElementPayment",
          contentKey: "medrite.checkin.stripe-element-payment",
        },
        initial: "formEntry",
        states: {
          formEntry: {
            meta: {
              componentName: "StripeElementPayment",
              contentKey: "medrite.checkin.stripe-element-payment",
            },
            on: {
              PAYMENT_SUCCESS: [
                {
                  // Setup intent success - no payment to process, go to success state
                  target: "setupIntentSuccess",
                  guard: ({ event }) => {
                    const setupIntentId = (
                      event.data as { setupIntentId?: string }
                    )?.setupIntentId;
                    return !!setupIntentId && setupIntentId.length > 0;
                  },
                  actions: assign({
                    setupIntentId: ({ event }) =>
                      (event.data as { setupIntentId?: string })?.setupIntentId,
                    paymentStatus: () => "success",
                  }),
                },
                {
                  // Payment intent success - needs processing
                  target: "processing",
                  guard: ({ event }) => {
                    const paymentIntentId = (
                      event.data as { paymentIntentId?: string }
                    )?.paymentIntentId;
                    return !!paymentIntentId && paymentIntentId.length > 0;
                  },
                  actions: assign({
                    paymentIntentId: ({ event }) =>
                      (event.data as { paymentIntentId?: string })
                        ?.paymentIntentId,
                  }),
                },
              ],
              PAYMENT_FAILED: {
                target: "failed",
                actions: assign({
                  paymentError: ({ event }) =>
                    (
                      event as {
                        paymentError?: { code: string; message: string };
                      }
                    ).paymentError,
                }),
              },
              BACK: [
                {
                  guard: ({ context }) =>
                    (context as { previousTreatmentState?: string })
                      .previousTreatmentState === "specificTreatment",
                  target: "#medrite-checkin.specificTreatment",
                  actions: assign(createClearedPaymentContext()),
                },
                {
                  guard: ({ context }) =>
                    (context as { previousTreatmentState?: string })
                      .previousTreatmentState === "defaultTreatment",
                  target: "#medrite-checkin.defaultTreatment",
                  actions: assign(createClearedPaymentContext()),
                },
                {
                  target: "#medrite-checkin.storefrontBrowse",
                  actions: assign(createClearedPaymentContext()),
                },
              ],
            },
          },
          processing: {
            meta: {
              componentName: "LoadingSpinner",
              contentKey: "medrite.checkin.processing-payment",
            },
            invoke: {
              id: "processStripeElementPayment",
              src: "processStripeElementPayment",
              input: ({ context }) => ({
                paymentIntentId: context.paymentIntentId as string,
                amount: context.amount as number,
                consumerId: context.consumerId as string,
                userId: context.userId as string,
                collectedBy: "kiosk-qr",
              }),
              onDone: {
                target: "#medrite-checkin.completeDefault",
                actions: assign({
                  paymentStatus: () => "success",
                  partialPaymentId: ({ event }) =>
                    (event.output as { partialPaymentId?: string })
                      .partialPaymentId,
                }),
              },
              onError: {
                target: "failed",
                actions: assign({
                  paymentStatus: () => "failure",
                  paymentError: ({ event }) => event.error,
                }),
              },
            },
          },
          setupIntentSuccess: {
            meta: {
              componentName: "StripeElementPayment",
              contentKey: "medrite.checkin.stripe-element-payment",
            },
            after: {
              3000: {
                target: "#medrite-checkin.completeCardOnFile",
              },
            },
          },
          failed: {
            entry: logFlowCompleteAction("failed"),
            meta: {
              componentName: "FrontDeskErrorScreen",
              contentKey: "medrite.checkin.write-failure",
            },
          },
        },
      },

      storefrontBrowse: {
        meta: {
          componentName: "StorefrontComponent",
          contentKey: "medrite.checkin.storefront-browse",
        },
        entry: [
          // Show the pre-browse modal when entering this state (unless coming back from treatment)
          assign(({ context }) => ({
            showPreBrowseModal: context.showPreBrowseModal !== false,
          })),
        ],
        on: {
          // New event: User selects "Urgent Care" from modal
          // Routes based on whether appointment exists and treatment changed
          SELECT_URGENT_CARE: [
            {
              // Case 1: Appointment exists AND treatment changed → replace bought treatment
              guard: and(["hasExistingAppointment", "isTreatmentChanged"]),
              target: "#medrite-checkin.replacingBoughtTreatment",
              // biome-ignore lint/suspicious/noExplicitAny: XState context types
              actions: assign(({ context }: any) =>
                createDefaultTreatmentContextUpdate(context),
              ),
            },
            {
              // Case 2: Appointment exists AND treatment same → skip to treatment state
              guard: "hasExistingAppointment",
              target: "#medrite-checkin.routeToTreatmentState",
              // biome-ignore lint/suspicious/noExplicitAny: XState context types
              actions: assign(({ context }: any) =>
                createDefaultTreatmentContextUpdate(context),
              ),
            },
            {
              // Case 3: No appointment → normal flow (create user/consumer/appointment)
              target: "#medrite-checkin.createUserAndConsumer",
              // biome-ignore lint/suspicious/noExplicitAny: XState context types
              actions: assign(({ context }: any) =>
                createDefaultTreatmentContextUpdate(context),
              ),
            },
          ],
          // New event: User clicks "Browse All Services" - just closes modal
          BROWSE_ALL: {
            actions: assign({
              showPreBrowseModal: false,
            }),
          },
          // User adds specific service to visit - routes based on appointment state
          SUBMIT: [
            {
              // Case 1: Appointment exists AND treatment changed → replace bought treatment
              guard: ({ context, event }) =>
                isTreatmentChangedFromEvent(
                  context,
                  (event as { data?: { hrtIds?: number[] } }).data,
                ),
              target: "#medrite-checkin.replacingBoughtTreatment",
              actions: [
                // biome-ignore lint/suspicious/noExplicitAny: XState context types
                assign(({ context, event }: any) =>
                  createSpecificTreatmentContext(
                    event.data,
                    context.hrts as HrtType[] | undefined,
                  ),
                ),
              ],
            },
            {
              // Case 2: Appointment exists AND treatment same → skip to treatment state
              guard: "hasExistingAppointment",
              target: "#medrite-checkin.routeToTreatmentState",
              actions: [
                // biome-ignore lint/suspicious/noExplicitAny: XState context types
                assign(({ context, event }: any) =>
                  createSpecificTreatmentContext(
                    event.data,
                    context.hrts as HrtType[] | undefined,
                  ),
                ),
              ],
            },
            {
              // Case 3: No appointment → normal flow (create user/consumer/appointment)
              target: "#medrite-checkin.createUserAndConsumer",
              actions: [
                // biome-ignore lint/suspicious/noExplicitAny: XState context types
                assign(({ context, event }: any) =>
                  createSpecificTreatmentContext(
                    event.data,
                    context.hrts as HrtType[] | undefined,
                  ),
                ),
              ],
            },
          ],
          ADD_INSURANCE: {
            target: "#medrite-checkin.insuring",
            actions: [
              assign({
                addingInsuranceFromStorefront: true,
              }),
            ],
          },
          SET_PRICING_MAP: {
            actions: [
              assign(({ context, event }) => {
                const eventData = event.data as { newPricingMap?: unknown };
                const existingPricingMap =
                  (context.pricingMap as Record<string, unknown>) ?? {};
                const newPricingMap =
                  (eventData.newPricingMap as Record<string, unknown>) ?? {};

                // Merge existing pricing (defaultHrt) with new pricing (storefront)
                const mergedPricingMap = {
                  ...existingPricingMap,
                  ...newPricingMap,
                };

                return {
                  ...context,
                  pricingMap: mergedPricingMap,
                };
              }),
            ],
          },
          BACK: [
            {
              guard: ({ context }) =>
                context.isWorkersComp === true ||
                context.isTravelInsurance === true,
              target: "#medrite-checkin.intaking",
            },
            {
              // Return user whose insurance auto-verified — no interactive
              // insurance screens were shown, so skip back to intake
              guard: ({ context }) =>
                (context._insuranceOutput as InsuranceOutput | undefined)
                  ?.autoVerifiedSuccess === true,
              target: "#medrite-checkin.intaking",
            },
            {
              target: "#medrite-checkin.insuring",
            },
          ],
        },
      },
      specificTreatment: {
        meta: {
          componentName: "TreatmentPurchase",
          contentKey: "medrite.checkin.default-treatment",
        },
        on: {
          PAY_NOW: [
            {
              guard: and(["hasExistingAppointment", "isTreatmentChanged"]),
              target: "replacingBoughtTreatment",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_NOW",
                previousTreatmentState: "specificTreatment",
              }),
            },
            {
              target: "preparePayment",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_NOW",
                previousTreatmentState: "specificTreatment",
              }),
            },
          ],
          PAY_AT_DESK: [
            {
              guard: and(["hasExistingAppointment", "isTreatmentChanged"]),
              target: "replacingBoughtTreatment",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_AT_DESK",
                previousTreatmentState: "specificTreatment",
              }),
            },
            {
              target: "completeFrontDesk",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_AT_DESK",
                previousTreatmentState: "specificTreatment",
              }),
            },
          ],
          PAY_LATER: [
            {
              guard: and(["hasExistingAppointment", "isTreatmentChanged"]),
              target: "replacingBoughtTreatment",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_LATER",
                previousTreatmentState: "specificTreatment",
              }),
            },
            {
              target: "preparePayment",
              actions: assign({
                pricingMode: ({ event }) =>
                  (event as { data?: { useInsurance?: boolean } }).data
                    ?.useInsurance
                    ? "INSURED"
                    : "CASH",
                paymentRoute: "PAY_LATER",
                previousTreatmentState: "specificTreatment",
              }),
            },
          ],
          COMPLETE: {
            target: "completeDefault",
            actions: assign({
              paymentRoute: "COMPLETE",
              previousTreatmentState: "specificTreatment",
            }),
          },
          SET_PRICING_MAP: {
            actions: [
              assign(({ context, event }) => {
                console.log("SET_PRICING_MAP", event);
                const eventData = event.data as { newPricingMap?: unknown };
                const existingPricingMap =
                  (context.pricingMap as Record<string, unknown>) ?? {};
                const newPricingMap =
                  (eventData.newPricingMap as Record<string, unknown>) ?? {};

                // Merge existing pricing (defaultHrt) with new pricing (storefront)
                return {
                  ...context,
                  pricingMap: {
                    ...existingPricingMap,
                    ...newPricingMap,
                  },
                };
              }),
            ],
          },
          SET_PRICING_MODE: {
            actions: [
              assign(({ context, event }) => {
                const { hrtId, mode } = event.data as {
                  hrtId: string;
                  mode: "CASH" | "INSURED";
                };
                const existingPricingMap =
                  (context.pricingMap as Record<string, unknown>) ?? {};
                const existingHrtPricing = existingPricingMap[hrtId] as
                  | Record<string, unknown>
                  | undefined;

                return {
                  pricingMap: {
                    ...existingPricingMap,
                    [hrtId]: {
                      ...existingHrtPricing,
                      mode,
                    },
                  },
                };
              }),
            ],
          },
          BACK: { target: "storefrontBrowse" },
        },
      },
      completeDefault: {
        entry: logFlowCompleteAction("completeDefault"),
        meta: {
          componentName: "FinalScreen",
          contentKey: "medrite.checkin.complete.default",
        },
        on: {
          FINISH: {
            target: "welcome",
          },
        },
      },

      completeWorkersCompEPSOrTravel: {
        entry: logFlowCompleteAction("completeWorkersCompEPSOrTravel"),
        meta: {
          componentName: "FinalScreen",
          contentKey: "medrite.checkin.complete.workers-comp-eps-or-travel",
        },
        on: {
          FINISH: {
            target: "welcome",
          },
        },
      },

      completeCardOnFile: {
        entry: logFlowCompleteAction("completeCardOnFile"),
        meta: {
          componentName: "FinalScreen",
          contentKey: "medrite.checkin.complete.card-on-file",
        },
        initial: "route",
        // Every substate repeats the parent meta: the renderer resolves the
        // screen from the active child, so the FinalScreen never changes.
        states: {
          route: {
            meta: {
              componentName: "FinalScreen",
              contentKey: "medrite.checkin.complete.card-on-file",
            },
            always: [
              {
                guard: "shouldRecordCardOnFileConsent",
                target: "recordingConsent",
              },
              { target: "done" },
            ],
          },
          // Records the card authorization for a card saved via SetupIntent.
          // Failure is logged by the actor and never leaves the FinalScreen.
          recordingConsent: {
            meta: {
              componentName: "FinalScreen",
              contentKey: "medrite.checkin.complete.card-on-file",
            },
            // FinalScreen holds the Okay button while this is true, so a tap
            // cannot tear down the machine mid-write.
            entry: assign({ cardAuthRecording: () => true }),
            exit: assign({ cardAuthRecording: () => false }),
            // Bound the wait: never hold the patient on Okay for long.
            after: {
              [CARD_AUTH_RECORDING_TIMEOUT_MS]: {
                target: "done",
                actions: [
                  assign({ cardAuthRecorded: () => true }),
                  ({ context }) => {
                    datadogLogs.logger.warn(
                      "[Fleet] Card-on-file consent timed out",
                      {
                        action: "card_on_file_consent_timeout",
                        metadata: {
                          kioskId: getKioskId(),
                          consumerId: context.consumerId ?? null,
                          setupIntentId: context.setupIntentId ?? null,
                        },
                      },
                    );
                  },
                ],
              },
            },
            invoke: {
              id: "recordCardOnFileConsent",
              src: "recordCardOnFileConsent",
              input: ({ context }) => ({
                consumerId: context.consumerId as string,
                userId: context.userId as string,
                orgId: Number(context.orgId),
                setupIntentId: context.setupIntentId as string,
              }),
              onDone: {
                target: "done",
                actions: assign({ cardAuthRecorded: () => true }),
              },
              onError: {
                target: "done",
                actions: assign({ cardAuthRecorded: () => true }),
              },
            },
          },
          done: {
            meta: {
              componentName: "FinalScreen",
              contentKey: "medrite.checkin.complete.card-on-file",
            },
          },
        },
        on: {
          FINISH: {
            target: "welcome",
            actions: assign({ cardAuthRecorded: () => undefined }),
          },
        },
      },

      completePayment: {
        entry: logFlowCompleteAction("completePayment"),
        meta: {
          componentName: "FinalScreen",
          contentKey: "medrite.checkin.complete.payment",
        },
        on: {
          FINISH: {
            target: "welcome",
          },
        },
      },
      completePayLater: {
        entry: logFlowCompleteAction("completePayLater"),
        meta: {
          componentName: "FinalScreen",
          contentKey: "medrite.checkin.complete.later",
        },
        on: {
          FINISH: {
            target: "welcome",
          },
        },
      },
      markingAsArrived: {
        meta: {
          componentName: "LoadingSpinner",
          contentKey: "medrite.checkin.marking-arrived",
        },
        invoke: {
          src: fromPromise(
            // biome-ignore lint/suspicious/noExplicitAny: XState context types
            async ({ input }: { input: any }) => {
              if (!input.orgId) {
                throw new Error("markAsArrived skipped: missing orgId");
              }
              await markAsArrived(
                input.appointmentId,
                input.ehrAppointmentId,
                input.orgId,
              );
            },
          ),
          input: ({ context }) => {
            const returningOutput = context._returningPatientOutput as
              | ReturningPatientOutput
              | undefined;
            return {
              appointmentId: returningOutput?.selectedAppointmentId ?? "",
              ehrAppointmentId: returningOutput?.selectedEhrAppointmentId ?? "",
              orgId: Number(context.orgId ?? 0),
            };
          },
          onDone: { target: "completeFrontDesk" },
          onError: {
            target: "completeFrontDesk",
            // biome-ignore lint/suspicious/noExplicitAny: XState context types
            actions: ({ event }: any) => {
              console.error("[markAsArrived] failed:", event.error);
            },
          },
        },
      },

      completeFrontDesk: {
        entry: logFlowCompleteAction("completeFrontDesk"),
        meta: {
          componentName: "FinalScreen",
          contentKey: "medrite.checkin.complete.front-desk",
        },
        on: {
          FINISH: {
            target: "welcome",
          },
        },
      },
      completeGoToFrontDesk: {
        meta: {
          componentName: "GoToFrontDeskScreen",
          contentKey: "medrite.checkin.complete.go-to-front-desk",
        },
        on: {
          FINISH: {
            target: "welcome",
          },
        },
      },
    },
  });
};
